import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  AppBar,
  Toolbar,
  Slide,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Tabs,
  Tab,
  Checkbox,
  Menu,
  TablePagination,
  InputAdornment,
  Chip,
  Popover,
  Badge,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityIcon from '@mui/icons-material/Visibility';
import LockIcon from '@mui/icons-material/Lock';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import RefreshIcon from '@mui/icons-material/Refresh';
import EmailIcon from '@mui/icons-material/Email';
import DescriptionIcon from '@mui/icons-material/Description';
import EventSeatIcon from '@mui/icons-material/EventSeat';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import MainLayout from '../layout/MainLayout';
import api from '../../services/api';
import AdminVendorRegistration from './AdminVendorRegistration';
import DuplicateUserManager from './DuplicateUserManager';
import DisregardEmailButton from './DisregardEmailButton';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import BoothSelectionMap from '../vendor/BoothSelectionMap';
import StyledButton from '../common/StyledButton';
import BoothChip, { BoothChipList } from '../common/BoothChip';
import CategoryChip from '../common/CategoryChip';
import { useAuth } from '../../context/AuthContext';
import { matchesSearch as matchesSearchFields } from '../../utils/matchesSearch';
import {
  getVendorContactEmail,
  getVendorContactName,
  getVendorIdentityLines,
  isAdminPlaceholderVendor,
} from '../../utils/vendorContact';

const CELL_COLORS = {
  booth: '#1976d2',
  booth_unavailable: '#424242',
  booth_assigned: '#4caf50',
  walkway: '#bdbdbd',
  food: '#ffb300',
  empty: '#fff',
  selected: '#4caf50',
};

const BASE_CELL_SIZE = 30;

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// Payments columns for the table
const PAYMENT_COLUMNS = [
  { header: 'Vendor Name', field: 'business_name' },
  { header: 'Email', field: 'email' },
  { header: 'Booth', field: 'booth_number' },
  { header: 'Status', field: 'status' },
  { header: 'Stripe ID', field: 'stripe_payment_intent_id' }
];

// Tab identifiers (kept as numeric values so the rest of the file's
// `activeTab === N` checks continue to work). The USERS and WEBHOOKS tabs are
// gated to super admins.
const TAB_VENDORS = 0;
const TAB_BOOTHS = 1;
const TAB_USERS = 2;
const TAB_WEBHOOKS = 3;
const TAB_PAYMENTS = 4;
const TAB_PENDING = 5;
const TAB_INCOMPLETE = 6;
const TAB_DUPLICATES = 7;
const SUPER_ONLY_TABS = new Set([TAB_USERS, TAB_WEBHOOKS]);

// Map of string keys → numeric tab indices, so callers (route components) can
// use readable names while the legacy `activeTab === N` checks keep working.
const TAB_KEY_TO_INDEX = {
  vendors: TAB_VENDORS,
  booths: TAB_BOOTHS,
  users: TAB_USERS,
  webhooks: TAB_WEBHOOKS,
  payments: TAB_PAYMENTS,
  pending: TAB_PENDING,
  incomplete: TAB_INCOMPLETE,
  duplicates: TAB_DUPLICATES,
};

// Renders one of the legacy admin tab panels inside the new sidebar layout.
// When `forcedTab` is provided the tab bar and top header are hidden — the
// caller (a page component under /admin/...) is in charge of the chrome.
const AdminDashboard = ({ forcedTab = null, embedded = false } = {}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isSuperAdmin } = useAuth();

  // State declarations
  const [vendors, setVendors] = useState([]);
  const [pendingVendors, setPendingVendors] = useState([]);
  const [booths, setBooths] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pendingLoading, setPendingLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [addVendorDialog, setAddVendorDialog] = useState(false);
  const [addAdminDialog, setAddAdminDialog] = useState(false);
  const [addUserDialog, setAddUserDialog] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [selectedBooth, setSelectedBooth] = useState(null);
  const [selectedVendors, setSelectedVendors] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState(false);
  const [deleteDetails, setDeleteDetails] = useState({
    vendorCount: 0,
    boothRegistrations: 0,
    userAccounts: 0,
    userCount: 0
  });
  const [newAdminData, setNewAdminData] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
  });
  const [newUserData, setNewUserData] = useState({
    email: '',
    firstName: '',
    lastName: '',
    vendorId: '', // For linking to existing vendor
  });
  const [vendorsNeedingUsers, setVendorsNeedingUsers] = useState([]);
  const [mapDialog, setMapDialog] = useState(false);
  const [selectedCell, setSelectedCell] = useState(null);
  const [editDialog, setEditDialog] = useState(false);
  const [editData, setEditData] = useState({
    cell_type: '',
    cell_name: '',
    has_electricity: false,
    section: '',
    is_corner: false,
    is_available: true,
  });
  const [zoom, setZoom] = useState(1);
  const [boothFilter, setBoothFilter] = useState('all');
  const [boothSearch, setBoothSearch] = useState('');
  const [userSearch, setUserSearch] = useState('');
  const [webhookSearch, setWebhookSearch] = useState('');
  const forcedTabIndex =
    forcedTab != null && Object.prototype.hasOwnProperty.call(TAB_KEY_TO_INDEX, forcedTab)
      ? TAB_KEY_TO_INDEX[forcedTab]
      : null;
  const [activeTab, setActiveTab] = useState(
    forcedTabIndex != null ? forcedTabIndex : parseInt(searchParams.get('tab') || '0')
  );
  const [roleFilter, setRoleFilter] = useState('all');
  const [sortConfig, setSortConfig] = useState({
    key: 'business_name',
    direction: 'asc'
  });
  const [editVendorDialog, setEditVendorDialog] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const REFRESH_INTERVAL = 30000; // 30 seconds
  const [viewDetailsDialog, setViewDetailsDialog] = useState(false);
  const [selectedVendorDetails, setSelectedVendorDetails] = useState(null);
  const [editUserDialog, setEditUserDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [editUserData, setEditUserData] = useState({
    email: '',
    firstName: '',
    lastName: '',
  });
  const [resetPasswordDialog, setResetPasswordDialog] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [webhookEvents, setWebhookEvents] = useState([]);
  const [webhookLoading, setWebhookLoading] = useState(false);
  const [webhookError, setWebhookError] = useState('');
  const [webhookEventType, setWebhookEventType] = useState('all');
  const [webhookPage, setWebhookPage] = useState(0);
  const [webhookRowsPerPage, setWebhookRowsPerPage] = useState(10);
  const [webhookTotalCount, setWebhookTotalCount] = useState(0);
  const [selectedWebhooks, setSelectedWebhooks] = useState([]);
  const [deleteWebhookDialog, setDeleteWebhookDialog] = useState(false);
  const [payments, setPayments] = useState([]);
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  const [paymentsError, setPaymentsError] = useState('');
  const [paymentStatusFilter, setPaymentStatusFilter] = useState('succeeded');
  const [paymentSearch, setPaymentSearch] = useState('');
  const [vendorAssignmentFilter, setVendorAssignmentFilter] = useState('all');
  const [vendorSearch, setVendorSearch] = useState('');
  const [vendorPayments, setVendorPayments] = useState([]);
  const [vendorPaymentsLoading, setVendorPaymentsLoading] = useState(false);
  const [vendorPaymentsError, setVendorPaymentsError] = useState('');
  const [refundLoading, setRefundLoading] = useState({}); // { [paymentId]: boolean }
  const [createdByFilter, setCreatedByFilter] = useState('all');
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailDialogRecipients, setEmailDialogRecipients] = useState([]);
  const [emailSubject, setEmailSubject] = useState('');
  const [emailMessage, setEmailMessage] = useState('');
  const [emailSending, setEmailSending] = useState(false);
  const [emailResults, setEmailResults] = useState(null);
  const [vendorPaymentStatusFilter, setVendorPaymentStatusFilter] = useState('all');
  const [vendorRegistrationStatusFilter, setVendorRegistrationStatusFilter] = useState('all');
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [vendorExportAnchorEl, setVendorExportAnchorEl] = useState(null);

  // Definitions for the Vendors-tab dropdown filters. Keeping them in one
  // place makes it cheap to count active filters, render removable chips,
  // and bulk-reset from inside the filter popover.
  const vendorFilterDefs = [
    {
      key: 'assignment',
      label: 'Booth',
      value: vendorAssignmentFilter,
      default: 'all',
      reset: () => setVendorAssignmentFilter('all'),
      display: { assigned: 'Assigned', unassigned: 'Unassigned' },
    },
    {
      key: 'createdBy',
      label: 'Created by',
      value: createdByFilter,
      default: 'all',
      reset: () => setCreatedByFilter('all'),
      display: { admin: 'Admin', user: 'User' },
    },
    {
      key: 'payment',
      label: 'Payment',
      value: vendorPaymentStatusFilter,
      default: 'all',
      reset: () => setVendorPaymentStatusFilter('all'),
      display: {
        pending: 'Pending',
        succeeded: 'Completed',
        failed: 'Failed',
        canceled: 'Canceled',
        no_payment: 'No payment',
      },
    },
    {
      key: 'registration',
      label: 'Status',
      value: vendorRegistrationStatusFilter,
      default: 'all',
      reset: () => setVendorRegistrationStatusFilter('all'),
      display: {
        pending_approval: 'Pending',
        approved: 'Approved',
        denied: 'Denied',
        incomplete: 'Incomplete',
        complete: 'Complete',
      },
    },
  ];
  const activeVendorFilters = vendorFilterDefs.filter((f) => f.value !== f.default);
  const clearAllVendorFilters = () => activeVendorFilters.forEach((f) => f.reset());
  const [approvalDialog, setApprovalDialog] = useState(false);
  const [denialDialog, setDenialDialog] = useState(false);
  const [selectedPendingVendor, setSelectedPendingVendor] = useState(null);
  const [approvalNotes, setApprovalNotes] = useState('');
  const [denialReason, setDenialReason] = useState('');
  const [denialNotes, setDenialNotes] = useState('');
  const [bulkApprovalDialog, setBulkApprovalDialog] = useState(false);
  const [selectedPendingVendors, setSelectedPendingVendors] = useState([]);
  const [bulkApprovalNotes, setBulkApprovalNotes] = useState('');
  const [boothSelectionDialog, setBoothSelectionDialog] = useState(false);
  const [selectedVendorForBooth, setSelectedVendorForBooth] = useState(null);
  const [selectedPayments, setSelectedPayments] = useState([]);
  const [deletePaymentDialog, setDeletePaymentDialog] = useState(false);
  // Add state for tracking refund status
  const [refundStatuses, setRefundStatuses] = useState({});
  // Add state for refund check loading
  const [refundCheckLoading, setRefundCheckLoading] = useState(false);
  const [paymentFilterAnchorEl, setPaymentFilterAnchorEl] = useState(null);

  // Definitions for the Payments-tab filters. Mirrors the vendors-tab
  // pattern so we can show active filters as removable chips and support
  // bulk-resetting from inside the popover.
  const paymentFilterDefs = [
    {
      key: 'status',
      label: 'Status',
      value: paymentStatusFilter,
      default: 'all',
      reset: () => setPaymentStatusFilter('all'),
      display: {
        pending: 'Pending',
        succeeded: 'Succeeded',
        failed: 'Failed',
        canceled: 'Canceled',
      },
    },
  ];
  const activePaymentFilters = paymentFilterDefs.filter((f) => f.value !== f.default);
  const clearAllPaymentFilters = () => activePaymentFilters.forEach((f) => f.reset());
  const [incompleteRegistrations, setIncompleteRegistrations] = useState([]);
  const [incompleteLoading, setIncompleteLoading] = useState(false);
  const [markPaidLoading, setMarkPaidLoading] = useState({});
  const [markCompleteLoading, setMarkCompleteLoading] = useState({});
  const [helpDialogOpen, setHelpDialogOpen] = useState(false);
  const [helpReason, setHelpReason] = useState(null);

  const openHelp = (reason) => {
    setHelpReason(reason);
    setHelpDialogOpen(true);
  };

  const getHelpContent = (reason) => {
    switch (reason) {
      case 'handmade':
        return {
          title: 'Handmade Agreement Needed',
          message:
            'The vendor likely did not complete the step where they must agree to the Handmade Agreement during registration. Ask them to log in and continue their registration to accept the agreement.'
        };
      case 'no_booth':
        return {
          title: 'No Booth Selected',
          message:
            'The vendor has not selected a booth yet. They should log in to choose a booth. If needed, you can assign a booth to them from the Pending Approvals or Booths tab.'
        };
      case 'not_submitted':
        return {
          title: 'Not Submitted for Approval',
          message:
            "The vendor started but did not submit their registration for approval. They may have run out of time or decided not to finish. If you think this is an error, try reaching out using their contact information."
        };
      default:
        return { title: 'Help', message: 'More information is not available for this item.' };
    }
  };

  const canMarkVendorPaid = (vendor) => {
    if (!vendor || !Array.isArray(vendor.booths) || vendor.booths.length === 0) return false;
    // Find approved booths for this vendor
    const approvedBoothIds = vendor.booths
      .filter(b => (b.registration_status === 'approved' || b.registration_status === 'complete') && b.booth_id)
      .map(b => b.booth_id);
    if (approvedBoothIds.length === 0) return false;
    // Check if every approved booth has a succeeded payment
    const hasAllPayments = approvedBoothIds.every(boothId =>
      payments.some(p => p.vendor_id === vendor.id && p.booth_id === boothId && p.status === 'succeeded')
    );
    return !hasAllPayments;
  };

  // Effect to handle URL tab changes — only when not running under a page
  // wrapper that has already chosen the tab.
  useEffect(() => {
    if (forcedTabIndex != null) {
      if (activeTab !== forcedTabIndex) setActiveTab(forcedTabIndex);
      return;
    }
    let tabFromUrl = parseInt(searchParams.get('tab') || '0');
    if (!isSuperAdmin && SUPER_ONLY_TABS.has(tabFromUrl)) {
      tabFromUrl = TAB_VENDORS;
    }
    if (tabFromUrl !== activeTab) {
      setActiveTab(tabFromUrl);
    }
  }, [searchParams, activeTab, isSuperAdmin, forcedTabIndex]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    searchParams.set('tab', newValue.toString());
    setSearchParams(searchParams);
  };

  const fetchVendors = async () => {
    try {
      const vendorsRes = await api.get('/admin/vendors');
      console.log('Total vendors from API:', vendorsRes.data.length);
      console.log('Vendor IDs:', vendorsRes.data.map(v => v.id));
      setVendors(vendorsRes.data);
    } catch (err) {
      console.error('Error fetching vendors:', err);
      setError(err.response?.data?.message || 'Failed to fetch vendors');
    }
  };

  const fetchBoothsAndUsers = async () => {
    try {
      const [boothsRes, usersRes] = await Promise.all([
        api.get('/admin/booths'),
        api.get('/admin/users'),
      ]);
      setBooths(boothsRes.data);
      setUsers(usersRes.data);
    } catch (err) {
      console.error('Error fetching booths and users:', err);
      setError(err.response?.data?.message || 'Failed to fetch data');
    }
  };

  const fetchPayments = async () => {
    setPaymentsLoading(true);
    setPaymentsError('');
    try {
      const res = await api.get('/admin/payments');
      setPayments(res.data);
    } catch (err) {
      setPaymentsError(err.response?.data?.error || 'Failed to fetch payments');
    } finally {
      setPaymentsLoading(false);
    }
  };

  const fetchPendingVendors = async () => {
    setPendingLoading(true);
    try {
      const res = await api.get('/admin/vendors/pending');
      setPendingVendors(res.data);
    } catch (err) {
      console.error('Error fetching pending vendors:', err);
      setError(err.response?.data?.error || 'Failed to fetch pending vendors');
    } finally {
      setPendingLoading(false);
    }
  };

  const fetchBooths = async () => {
    try {
      const response = await api.get('/admin/booths');
      setBooths(response.data);
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to fetch booths');
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      console.log('Fetching data...');
      await Promise.all([
        fetchVendors(),
        fetchBoothsAndUsers(),
        fetchPayments(),
        fetchBooths()
      ]);
      setError('');
    } catch (err) {
      console.error('Error fetching data:', err);
      setError(err.response?.data?.message || 'Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const fetchWebhookEvents = async () => {
    try {
      setWebhookLoading(true);
      const response = await api.get('/webhooks/events', {
        params: {
          eventType: webhookEventType !== 'all' ? webhookEventType : undefined,
          search: webhookSearch.trim() || undefined,
          limit: webhookRowsPerPage,
          offset: webhookPage * webhookRowsPerPage
        }
      });
      const payload = response.data;
      // Tolerate both the legacy bare-array response and the newer
      // { rows, totalCount } envelope so older deployments keep working.
      const rows = Array.isArray(payload) ? payload : payload?.rows || [];
      const total = Array.isArray(payload)
        ? payload.length
        : payload?.totalCount ?? rows.length;
      setWebhookEvents(rows);
      setWebhookTotalCount(total);
    } catch (err) {
      setWebhookError(err.response?.data?.message || 'Failed to fetch webhook events');
    } finally {
      setWebhookLoading(false);
    }
  };

  const fetchIncompleteRegistrations = async () => {
    setIncompleteLoading(true);
    try {
      const res = await api.get('/admin/vendors/incomplete');
      setIncompleteRegistrations(res.data);
    } catch (err) {
      console.error('Error fetching incomplete registrations:', err);
      setError(err.response?.data?.error || 'Failed to fetch incomplete registrations');
    } finally {
      setIncompleteLoading(false);
    }
  };

  // Initial data fetch
  useEffect(() => {
    fetchData();
  }, []);

  // Auto-refresh effect for vendors table only
  useEffect(() => {
    let intervalId;

    if (autoRefresh && !addVendorDialog && !editVendorDialog) {
      intervalId = setInterval(() => {
        fetchVendors();
      }, REFRESH_INTERVAL);
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [autoRefresh, addVendorDialog, editVendorDialog]);

  useEffect(() => {
    if (activeTab === 3) { // Webhooks tab
      fetchWebhookEvents();
    }
  }, [activeTab, webhookEventType, webhookPage, webhookRowsPerPage, webhookSearch]);

  useEffect(() => {
    if (activeTab === 4) { // Payments tab
      fetchPayments();
    }
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === 5) { // Pending Approvals tab
      fetchPendingVendors();
    }
  }, [activeTab]);

  useEffect(() => {
    if (viewDetailsDialog && selectedVendorDetails?.id) {
      setVendorPaymentsLoading(true);
      setVendorPaymentsError('');
      api.get(`/admin/vendors/${selectedVendorDetails.id}/payments`)
        .then(res => setVendorPayments(res.data))
        .catch(err => setVendorPaymentsError(err.response?.data?.error || 'Failed to fetch payments'))
        .finally(() => setVendorPaymentsLoading(false));
    } else {
      setVendorPayments([]);
      setVendorPaymentsError('');
    }
  }, [viewDetailsDialog, selectedVendorDetails]);

  useEffect(() => {
    if (activeTab === 6) { // Incomplete Registrations tab
      fetchIncompleteRegistrations();
    }
  }, [activeTab]);

  const handleRemoveBoothAssignment = async (boothId) => {
    try {
      await api.delete(`/admin/booth-assignments/${boothId}`);
      setSuccess('Booth assignment removed successfully');
      fetchData();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to remove booth assignment');
    }
  };

  const handleZoomIn = () => {
    setZoom(prev => Math.min(prev + 0.2, 2));
  };

  const handleZoomOut = () => {
    setZoom(prev => Math.max(prev - 0.2, 0.5));
  };

  const handleResetZoom = () => {
    setZoom(1);
  };

  const handleCellClick = (cell) => {
    if (cell?.cell_type === 'booth') {
      setSelectedCell(cell);
      setEditData({
        cell_type: cell.cell_type,
        cell_name: cell.cell_name,
        has_electricity: cell.has_electricity,
        section: cell.section,
        is_corner: cell.is_corner,
        is_available: cell.is_available,
      });
      setEditDialog(true);
    }
  };

  const handleEditSubmit = async () => {
    try {
      await api.put(`/admin/booths/${selectedCell.id}/status`, {
        status: editData.is_available ? 'available' : 'unavailable'
      });
      setSuccess('Booth status updated successfully');
      setEditDialog(false);
      fetchData();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update booth status');
    }
  };

  const getCellColor = (cell) => {
    if (!cell) return CELL_COLORS.empty;
    if (cell === selectedCell) return CELL_COLORS.selected;
    if (cell.cell_type === 'booth') {
      if (cell.booth_status === 'unavailable') return CELL_COLORS.booth_unavailable;
      if (cell.is_registered) return CELL_COLORS.booth_assigned;
      return CELL_COLORS.booth;
    }
    return CELL_COLORS[cell.cell_type] || CELL_COLORS.empty;
  };

  // Find grid size
  const width = Math.max(...booths.map(b => b.x_coordinate), 0);
  const height = Math.max(...booths.map(b => b.y_coordinate), 0);

  // Build grid
  const grid = Array.from({ length: height }, (_, y) =>
    Array.from({ length: width }, (_, x) =>
      booths.find(c => c.x_coordinate === x + 1 && c.y_coordinate === y + 1)
    )
  );

  const handleAddAdmin = async () => {
    try {
      // Log the data being sent
      console.log('Sending admin data:', newAdminData);

      // Validate required fields
      if (!newAdminData.email || !newAdminData.password || !newAdminData.firstName || !newAdminData.lastName) {
        setError('All fields are required');
        return;
      }

      const response = await api.post('/admin/admins', newAdminData);
      console.log('Admin creation response:', response.data);
      
      setSuccess('Admin user added successfully');
      setAddAdminDialog(false);
      setNewAdminData({
        email: '',
        password: '',
        firstName: '',
        lastName: '',
      });

      // Force a refresh of the data
      await fetchData();
    } catch (err) {
      console.error('Error creating admin:', {
        error: err,
        response: err.response?.data,
        status: err.response?.status
      });
      setError(err.response?.data?.error || err.response?.data?.details || 'Failed to add admin user');
    }
  };

  const fetchVendorsNeedingUsers = async () => {
    try {
      const response = await api.get('/admin/vendors/need-user-accounts');
      setVendorsNeedingUsers(response.data);
    } catch (err) {
      console.error('Error fetching vendors needing users:', err);
    }
  };

  const handleAddUser = async () => {
    try {
      // Validate required fields
      if (!newUserData.email || !newUserData.firstName || !newUserData.lastName) {
        setError('All fields are required');
        return;
      }

      let response;
      let successMessage;

      if (newUserData.vendorId) {
        // Create user for existing vendor
        response = await api.post(`/admin/vendors/${newUserData.vendorId}/create-user`, {
          email: newUserData.email,
          firstName: newUserData.firstName,
          lastName: newUserData.lastName
        });
        successMessage = response.data.message;
      } else {
        // Create standalone user
        response = await api.post('/admin/users/create-with-invitation', {
          email: newUserData.email,
          firstName: newUserData.firstName,
          lastName: newUserData.lastName
        });
        successMessage = `User invitation sent successfully to ${newUserData.email}`;
      }

      console.log('User creation response:', response.data);
      
      setSuccess(successMessage);
      setAddUserDialog(false);
      setNewUserData({
        email: '',
        firstName: '',
        lastName: '',
        vendorId: '',
      });

      // Force a refresh of the data
      await fetchData();
      if (newUserData.vendorId) {
        await fetchVendorsNeedingUsers(); // Refresh vendors needing users
      }
    } catch (err) {
      console.error('Error creating user:', {
        error: err,
        response: err.response?.data,
        status: err.response?.status
      });
      setError(err.response?.data?.error || err.response?.data?.details || 'Failed to create user');
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      await api.put(`/admin/users/${userId}/role`, { role: newRole });
      setSuccess('User role updated successfully');
      fetchData();
    } catch (err) {
      console.error('Error updating user role:', err);
      setError(err.response?.data?.error || 'Failed to update user role');
    }
  };

  const handleDeleteVendors = async () => {
    try {
      // First, get details about what will be deleted
      const selectedVendorDetails = vendors.filter(v => selectedVendors.includes(v.id));
      const boothRegistrations = selectedVendorDetails.filter(v => v.booth_id).length;

      setDeleteDetails({
        vendorCount: selectedVendors.length,
        boothRegistrations,
        userAccounts: selectedVendorDetails.length // Each vendor has one user account
      });

      setDeleteConfirmDialog(true);
    } catch (err) {
      setError('Failed to prepare deletion details');
    }
  };

  const confirmDeleteVendors = async () => {
    try {
      const response = await api.delete('/admin/vendors', {
        data: { vendorIds: selectedVendors }
      });

      setSuccess(`Successfully deleted ${response.data.count} vendor(s). This included:
        - ${response.data.count} vendor profile(s)
        - ${deleteDetails.boothRegistrations} booth registration(s)
        - ${deleteDetails.userAccounts} user account(s) will be retained for historical purposes`);
      
      setDeleteConfirmDialog(false);
      setSelectedVendors([]);
      fetchData();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete vendors');
    }
  };

  const handleVendorSelect = (vendorIds) => {
    if (!Array.isArray(vendorIds)) {
      vendorIds = [vendorIds];
    }
    setSelectedVendors(prev => {
      const allSelected = vendorIds.every(id => prev.includes(id));
      if (allSelected) {
        return prev.filter(id => !vendorIds.includes(id));
      }
      return [...new Set([...prev, ...vendorIds])];
    });
  };

  const handleSelectAllVendors = () => {
    const allVendorIds = groupVendors.flatMap(v => v.vendorIds || [v.id]);
    if (selectedVendors.length === allVendorIds.length) {
      setSelectedVendors([]);
    } else {
      setSelectedVendors(allVendorIds);
    }
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const groupVendors = React.useMemo(() => {
    console.log('Starting grouping with vendors:', vendors.length);
    
    // First, let's deduplicate vendors based on ID
    const uniqueVendors = Array.from(new Map(vendors.map(v => [v.id, v])).values());
    console.log('Unique vendors after deduplication:', uniqueVendors.length);
    
    // Create a map to group vendors by vendor ID (not user_id)
    const groupedMap = uniqueVendors.reduce((acc, vendor) => {
      const key = vendor.id;
      
      // Get all payments for this vendor
      const vendorPayments = payments.filter(p => p.vendor_id === vendor.id);
      const latestPayment = vendorPayments.length > 0 
        ? vendorPayments.sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0]
        : null;
      const paymentStatus = latestPayment ? latestPayment.status : 'no_payment';
      
      // Debug logging for payment matching
      if (vendorPayments.length > 0) {
        console.log(`Vendor ${vendor.id} (${vendor.business_name}) payments:`, {
          numPayments: vendorPayments.length,
          latestPayment: {
            id: latestPayment.id,
            status: latestPayment.status,
            created_at: latestPayment.created_at
          }
        });
      }

      // Each vendor is its own entry with its booth_details
      acc.set(key, {
        ...vendor,
        booths: vendor.booth_details || [],
        payment_status: paymentStatus
      });

      return acc;
    }, new Map());

    const result = Array.from(groupedMap.values());
    console.log('Total vendors after grouping:', result.length);
    return result;
  }, [vendors, payments]);

  const sortedVendors = React.useMemo(() => {
    let sortableItems = [...groupVendors];
    if (sortConfig.key) {
      sortableItems.sort((a, b) => {
        if (sortConfig.key === 'booth_name') {
          const boothsA = a.booths.map(b => b.booth_name || '').join(', ');
          const boothsB = b.booths.map(b => b.booth_name || '').join(', ');
          if (sortConfig.direction === 'asc') {
            return boothsA.localeCompare(boothsB);
          }
          return boothsB.localeCompare(boothsA);
        } else {
          if (sortConfig.direction === 'asc') {
            return (a[sortConfig.key] || '').localeCompare(b[sortConfig.key] || '');
          }
          return (b[sortConfig.key] || '').localeCompare(a[sortConfig.key] || '');
        }
      });
    }
    return sortableItems;
  }, [groupVendors, sortConfig]);

  

  const handleEditVendor = (vendor) => {
    setSelectedVendor(vendor);
    setEditVendorDialog(true);
  };

  const handleVendorUpdate = async () => {
    await fetchData();
    setEditVendorDialog(false);
    setSelectedVendor(null);
  };

  const handleMarkVendorPaid = async (vendor) => {
    try {
      setMarkPaidLoading(prev => ({ ...prev, [vendor.id]: true }));
      await api.post(`/admin/vendors/${vendor.id}/mark-paid`);
      setSuccess('Vendor marked as paid');
      await Promise.all([fetchPayments(), fetchVendors()]);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to mark vendor as paid');
    } finally {
      setMarkPaidLoading(prev => ({ ...prev, [vendor.id]: false }));
    }
  };

  const handleMarkVendorComplete = async (vendor) => {
    try {
      setMarkCompleteLoading(prev => ({ ...prev, [vendor.id]: true }));
      await api.post(`/admin/vendors/${vendor.id}/mark-complete`);
      setSuccess('Vendor marked as complete');
      await fetchVendors();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to mark vendor as complete');
    } finally {
      setMarkCompleteLoading(prev => ({ ...prev, [vendor.id]: false }));
    }
  };

  const handleViewDetails = (vendor) => {
    navigate(`/admin/vendors/${vendor.id}?returnTab=${activeTab}`);
  };

  const handleUserSelect = (userId) => {
    setSelectedUsers(prev => {
      if (prev.includes(userId)) {
        return prev.filter(id => id !== userId);
      }
      return [...prev, userId];
    });
  };

  const handleSelectAllUsers = () => {
    if (selectedUsers.length === users.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(users.map(u => u.id));
    }
  };

  const handleDeleteUsers = async () => {
    try {
      // First, get details about what will be deleted
      const selectedUserDetails = users.filter(u => selectedUsers.includes(u.id));
      const vendorUsers = selectedUserDetails.filter(u => u.role === 'vendor');
      const adminUsers = selectedUserDetails.filter(u => u.role === 'admin');

      setDeleteDetails({
        userCount: selectedUsers.length,
        vendorCount: vendorUsers.length,
        adminCount: adminUsers.length,
        boothRegistrations: vendorUsers.length // Each vendor has one booth registration
      });

      setDeleteConfirmDialog(true);
    } catch (err) {
      setError('Failed to prepare deletion details');
    }
  };

  const confirmDeleteUsers = async () => {
    try {
      const response = await api.delete('/admin/users', {
        data: { userIds: selectedUsers }
      });

      setSuccess(`Successfully deleted ${response.data.count} user(s). This included:
        - ${deleteDetails.vendorCount} vendor user(s)
        - ${deleteDetails.adminCount} admin user(s)
        - ${deleteDetails.boothRegistrations} booth registration(s)`);
      
      setDeleteConfirmDialog(false);
      setSelectedUsers([]);
      fetchData();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete users');
    }
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setEditUserData({
      email: user.email,
      firstName: user.firstName,
      lastName: user.lastName,
    });
    setEditUserDialog(true);
  };

  const handleUserUpdate = async () => {
    try {
      await api.put(`/admin/users/${selectedUser.id}`, editUserData);
      setSuccess('User updated successfully');
      setEditUserDialog(false);
      fetchData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update user');
    }
  };

  const handleResetPassword = async () => {
    try {
      if (newPassword !== confirmPassword) {
        setPasswordError('Passwords do not match');
        return;
      }
      if (newPassword.length < 8) {
        setPasswordError('Password must be at least 8 characters long');
        return;
      }

      await api.post(`/admin/users/${selectedUser.id}/reset-password`, {
        password: newPassword
      });
      setSuccess('Password reset successfully');
      setResetPasswordDialog(false);
      setNewPassword('');
      setConfirmPassword('');
      setPasswordError('');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to reset password');
    }
  };

  const handleExportCSV = (data, filename) => {
    // Convert data to CSV format
    const headers = Object.keys(data[0]);
    const csvContent = [
      headers.join(','),
      ...data.map(row => headers.map(header => {
        const value = row[header];
        // Handle values that might contain commas
        return typeof value === 'string' && value.includes(',') ? `"${value}"` : value;
      }).join(','))
    ].join('\n');

    // Create and download the file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${filename}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleExportPDF = (data, filename, columns) => {
    const doc = new jsPDF();
    
    // Add title
    doc.setFontSize(16);
    doc.text(filename, 14, 15);
    
    // Add table
    autoTable(doc, {
      head: [columns.map(col => col.header)],
      body: data.map(row => columns.map(col => row[col.field])),
      startY: 25,
      styles: { fontSize: 8 },
      headStyles: { fillColor: [25, 118, 210] }
    });
    
    // Save the PDF
    doc.save(`${filename}.pdf`);
  };

  const getVendorExportData = () => {
    return filteredVendors.map(vendor => {
      // Same booths the table shows: booth_details mapped onto vendor.booths.
      // Denied registrations are hidden in the list, so leave them out here too.
      const boothNames = (Array.isArray(vendor.booths) ? vendor.booths : [])
        .filter((booth) => booth && booth.registration_status !== 'denied')
        .map((booth) => booth.booth_name || booth.cell_name || booth.name)
        .filter(Boolean);

      return {
        'Business Name': vendor.business_name,
        'Booth': boothNames.length > 0 ? boothNames.join(', ') : 'No booth assigned',
        'Phone': vendor.phone || '',
        'Email': vendor.email || ''
      };
    });
  };

  const getBoothExportData = () => {
    return booths
      .filter(booth => booth.cell_type === 'booth')
      .map(booth => ({
        'Booth Number': booth.cell_name,
        'Status': booth.booth_status === 'unavailable' ? 'Unavailable' : 
                 booth.is_registered ? 'Assigned' : 'Available',
        'Assigned Vendor': booth.vendor_name || '-',
        'Has Electricity': booth.has_electricity ? 'Yes' : 'No',
        'Section': booth.section || '',
        'Is Corner': booth.is_corner ? 'Yes' : 'No'
      }));
  };

  const getUserExportData = () => {
    return users.map(user => ({
      'Name': `${user.firstName} ${user.lastName}`,
      'Email': user.email,
      'Role': user.role,
      'Created At': new Date(user.createdAt).toLocaleDateString()
    }));
  };

  const handleWebhookSelect = (webhookId) => {
    setSelectedWebhooks(prev => {
      if (prev.includes(webhookId)) {
        return prev.filter(id => id !== webhookId);
      }
      return [...prev, webhookId];
    });
  };

  const handleSelectAllWebhooks = () => {
    if (selectedWebhooks.length === webhookEvents.length) {
      setSelectedWebhooks([]);
    } else {
      setSelectedWebhooks(webhookEvents.map(w => w.id));
    }
  };

  const handleDeleteWebhooks = async () => {
    try {
      await api.delete('/webhooks/events', {
        data: { ids: selectedWebhooks }
      });
      setSuccess(`Successfully deleted ${selectedWebhooks.length} webhook event(s)`);
      setDeleteWebhookDialog(false);
      setSelectedWebhooks([]);
      fetchWebhookEvents();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete webhook events');
    }
  };

  const handleRefresh = () => {
    switch (activeTab) {
      case 1: // Booths tab
        fetchBoothsAndUsers();
        break;
      case 2: // Users tab
        fetchBoothsAndUsers();
        break;
      case 3: // Webhooks tab
        fetchWebhookEvents();
        break;
      case 4: // Payments tab
        fetchPayments();
        break;
      default:
        break;
    }
  };

  const renderAddVendorDialog = () => (
    <Dialog
      open={addVendorDialog}
      onClose={() => setAddVendorDialog(false)}
      maxWidth="md"
      fullWidth
      TransitionComponent={Transition}
    >
      <DialogTitle>
        Add New Vendor
        <IconButton
          aria-label="close"
          onClick={() => setAddVendorDialog(false)}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <AdminVendorRegistration 
          open={addVendorDialog}
          onClose={(shouldRefresh) => {
            setAddVendorDialog(false);
            if (shouldRefresh) {
              fetchVendors();
            }
          }}
          mode="create"
        />
      </DialogContent>
    </Dialog>
  );

  const renderEditVendorDialog = () => (
    <Dialog
      open={editVendorDialog}
      onClose={() => setEditVendorDialog(false)}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        Edit Vendor
      </DialogTitle>
      <DialogContent>
        <AdminVendorRegistration 
          open={editVendorDialog}
          onClose={(shouldRefresh) => {
            setEditVendorDialog(false);
            if (shouldRefresh) {
              fetchVendors();
            }
          }}
          mode="edit"
          vendor={selectedVendor}
        />
      </DialogContent>
    </Dialog>
  );

  // Filter and search payments
  const filteredPayments = payments.filter(payment => {
    const matchesStatus = paymentStatusFilter === 'all' || payment.status === paymentStatusFilter;
    const matchesSearch = matchesSearchFields(payment, paymentSearch, [
      'business_name',
      'email',
      'firstName',
      'lastName',
      (p) => `${p.firstName || ''} ${p.lastName || ''}`.trim(),
      'booth_number',
      'stripe_payment_intent_id',
      'refund_id',
      'festival_name',
      'status',
      (p) => (p.amount !== undefined && p.amount !== null ? String(p.amount) : null),
    ]);
    return matchesStatus && matchesSearch;
  });

  // Filter and search vendors
  const filteredVendors = sortedVendors.filter(vendor => {
    const matchesAssignment =
      vendorAssignmentFilter === 'all' ||
      (vendorAssignmentFilter === 'assigned' && Array.isArray(vendor.booths) && vendor.booths.length > 0) ||
      (vendorAssignmentFilter === 'unassigned' && (!Array.isArray(vendor.booths) || vendor.booths.length === 0));
    const matchesSearch = matchesSearchFields(vendor, vendorSearch, [
      'business_name',
      'email',
      'contact_email',
      'firstName',
      'lastName',
      'contact_first_name',
      'contact_last_name',
      (v) => getVendorContactName(v),
      (v) => getVendorContactEmail(v),
      (v) => `${v.firstName || v.contact_first_name || ''} ${v.lastName || v.contact_last_name || ''}`.trim(),
      'phone',
      'description',
      'website',
      'address',
      'city',
      'state',
      'zip_code',
      'category_name',
      'registration_status',
      (v) => (Array.isArray(v.booth_details) ? v.booth_details.map((b) => b && b.booth_name) : null),
      (v) => (Array.isArray(v.booths) ? v.booths.map((b) => b && (b.booth_name || b.cell_name)) : null),
    ]);
    const matchesCreatedBy =
      createdByFilter === 'all' ||
      (createdByFilter === 'admin' && isAdminPlaceholderVendor(vendor)) ||
      (createdByFilter === 'user' && !isAdminPlaceholderVendor(vendor));
    // Payment Status filter
    const vendorPayments = payments.filter(p => p.vendor_id === vendor.id);
    const latestPayment = vendorPayments.sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0];
    let paymentStatus = latestPayment ? latestPayment.status : 'no_payment';
    if (vendorPaymentStatusFilter !== 'all') {
      if (vendorPaymentStatusFilter === 'no_payment' && paymentStatus !== 'no_payment') return false;
      if (vendorPaymentStatusFilter !== 'no_payment' && paymentStatus !== vendorPaymentStatusFilter) return false;
    }

    // Registration Status filter (use the same computed logic as display)
    const hasApprovedBooth = Array.isArray(vendor.booths) && vendor.booths.some(b => b.registration_status === 'approved');
    const hasPendingBooth = Array.isArray(vendor.booths) && vendor.booths.some(b => b.registration_status === 'pending_approval');
    const computedRegStatus = hasApprovedBooth ? 'approved' : hasPendingBooth ? 'pending_approval' : (vendor.registration_status || 'incomplete');
    if (vendorRegistrationStatusFilter !== 'all' && computedRegStatus !== vendorRegistrationStatusFilter) {
      return false;
    }
    return matchesAssignment && matchesSearch && matchesCreatedBy;
  });

  // Refund handler
  const handleRefund = async (paymentId) => {
    setRefundLoading(prev => ({ ...prev, [paymentId]: true }));
    try {
      await api.post(`/admin/payments/${paymentId}/refund`);
      // Refresh payments after refund
      const res = await api.get(`/admin/vendors/${selectedVendorDetails.id}/payments`);
      setVendorPayments(res.data);
      setSuccess('Refund issued successfully');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to issue refund');
    } finally {
      setRefundLoading(prev => ({ ...prev, [paymentId]: false }));
    }
  };

  // Handler to open email dialog for selected vendors
  const handleOpenBulkVendorEmailDialog = () => {
    const emails = vendors
      .filter(v => selectedVendors.includes(v.id))
      .map((v) => getVendorContactEmail(v))
      .filter(Boolean);
    setEmailDialogRecipients(emails);
    setEmailSubject('');
    setEmailMessage('');
    setEmailResults(null);
    setEmailDialogOpen(true);
  };
  // Handler to open email dialog for a single vendor
  const handleOpenSingleVendorEmailDialog = (vendor) => {
    const email = getVendorContactEmail(vendor);
    setEmailDialogRecipients(email ? [email] : []);
    setEmailSubject('');
    setEmailMessage('');
    setEmailResults(null);
    setEmailDialogOpen(true);
  };
  // Handler to send email(s)
  const handleSendEmail = async () => {
    setEmailSending(true);
    setEmailResults(null);
    try {
      const res = await api.post('/admin/email-users', {
        emails: emailDialogRecipients,
        subject: emailSubject,
        message: emailMessage
      });
      setEmailResults(res.data.results);
    } catch (err) {
      setEmailResults([{ email: 'All', success: false, error: err.response?.data?.error || 'Failed to send email' }]);
    } finally {
      setEmailSending(false);
    }
  };

  const handleApproveVendor = async (vendorId) => {
    try {
      await api.post(`/admin/vendors/${vendorId}/approve`, {
        notes: approvalNotes
      });
      setSuccess('Vendor approved successfully');
      setApprovalDialog(false);
      setApprovalNotes('');
      setSelectedPendingVendor(null);
      fetchPendingVendors();
      fetchVendors(); // Refresh main vendors list
    } catch (err) {
      console.error('Error approving vendor:', err);
      setError(err.response?.data?.error || 'Failed to approve vendor');
    }
  };

  const handleDenyVendor = async (vendorId) => {
    try {
      await api.post(`/admin/vendors/${vendorId}/deny`, {
        denialReason,
        notes: denialNotes,
        boothIds: selectedPendingVendor ? [selectedPendingVendor.booth_id] : []
      });
      setSuccess('Vendor denied successfully');
      setDenialDialog(false);
      setDenialReason('');
      setDenialNotes('');
      setSelectedPendingVendor(null);
      fetchPendingVendors();
      fetchVendors(); // Refresh main vendors list
    } catch (err) {
      console.error('Error denying vendor:', err);
      setError(err.response?.data?.error || 'Failed to deny vendor');
    }
  };

  const handleResetVendorToIncomplete = async (vendorId) => {
    try {
      await api.post(`/admin/vendors/${vendorId}/reset-to-incomplete`);
      setSuccess('Vendor reset to incomplete status successfully. They can now re-register for next year.');
      fetchPendingVendors();
      fetchVendors(); // Refresh main vendors list
    } catch (err) {
      console.error('Error resetting vendor:', err);
      setError(err.response?.data?.error || 'Failed to reset vendor');
    }
  };

  const handleBulkApprove = async () => {
    try {
      await api.post('/admin/vendors/bulk-approve', {
        vendorIds: selectedPendingVendors,
        notes: bulkApprovalNotes
      });
      setSuccess(`${selectedPendingVendors.length} vendors approved successfully`);
      setBulkApprovalDialog(false);
      setBulkApprovalNotes('');
      setSelectedPendingVendors([]);
      fetchPendingVendors();
      fetchVendors(); // Refresh main vendors list
    } catch (err) {
      console.error('Error bulk approving vendors:', err);
      setError(err.response?.data?.error || 'Failed to bulk approve vendors');
    }
  };

  const handlePendingVendorSelect = (vendorId) => {
    setSelectedPendingVendors(prev => {
      if (prev.includes(vendorId)) {
        return prev.filter(id => id !== vendorId);
      }
      return [...prev, vendorId];
    });
  };

  const handleSelectAllPendingVendors = () => {
    if (selectedPendingVendors.length === pendingVendors.length) {
      setSelectedPendingVendors([]);
    } else {
      setSelectedPendingVendors(pendingVendors.map(v => v.vendor_id));
    }
  };

  const handleBoothAssignmentChange = async (vendorId, boothData) => {
    try {
      setLoading(true);

      // Remove the current booth assignment if it exists
      if (selectedVendorForBooth?.booth_id) {
        await api.delete(`/admin/booth-assignments/${selectedVendorForBooth.booth_id}`);
      }

      // Create new booth assignment
      await api.post('/admin/booth-assignments', {
        vendorId: vendorId,
        boothId: boothData.id
      });

      setSuccess('Booth assignment updated successfully');
      fetchPendingVendors();
      fetchBooths();
    } catch (err) {
      console.error('Error updating booth assignment:', err);
      setError(err.response?.data?.error || 'Failed to update booth assignment');
    } finally {
      setLoading(false);
      setBoothSelectionDialog(false);
      setSelectedVendorForBooth(null);
    }
  };

  const handleResetBoothStatus = async (vendor) => {
    try {
      setLoading(true);
      const response = await api.post(`/admin/vendors/${vendor.id}/reset-booth-status`);
      setSuccess(`Reset booth status for ${vendor.business_name}. ${response.data.message}`);
      await fetchVendors(); // Refresh vendor data
    } catch (err) {
      console.error('Error resetting booth status:', err);
      setError(err.response?.data?.error || 'Failed to reset booth status');
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentSelect = (paymentId) => {
    setSelectedPayments(prev => {
      if (prev.includes(paymentId)) {
        return prev.filter(id => id !== paymentId);
      }
      return [...prev, paymentId];
    });
  };

  const handleSelectAllPayments = () => {
    if (selectedPayments.length === filteredPayments.length) {
      setSelectedPayments([]);
    } else {
      setSelectedPayments(filteredPayments.map(p => p.id));
    }
  };

  // Add function to check refund status
  const checkRefundStatus = async (payment) => {
    console.log('Checking refund status for payment:', payment);
    if (payment.status !== 'succeeded' || payment.refund_id) {
      console.log('Payment can be deleted - not succeeded or already refunded in DB');
      return true;
    }

    try {
      const response = await api.get(`/admin/payments/${payment.id}`);
      const updatedPayment = response.data;
      console.log('Updated payment data:', updatedPayment);
      
      setRefundStatuses(prev => ({
        ...prev,
        [payment.id]: updatedPayment.refund_id ? true : false
      }));
      
      return updatedPayment.refund_id ? true : false;
    } catch (err) {
      console.error('Error checking refund status:', err);
      return false;
    }
  };

  // Add function to check all succeeded payments
  const checkAllRefundStatuses = async () => {
    try {
      setRefundCheckLoading(true);
      const succeededPayments = filteredPayments.filter(p => p.status === 'succeeded' && !p.refund_id);
      console.log('Checking refund status for payments:', succeededPayments);

      const newStatuses = {};
      for (const payment of succeededPayments) {
        const response = await api.get(`/admin/payments/${payment.id}`);
        const updatedPayment = response.data;
        newStatuses[payment.id] = updatedPayment.refund_id ? true : false;
      }

      setRefundStatuses(newStatuses);
      setSuccess('Refund status check completed');
    } catch (err) {
      console.error('Error checking refund statuses:', err);
      setError('Failed to check refund statuses');
    } finally {
      setRefundCheckLoading(false);
    }
  };

  const handleDeletePayments = async () => {
    try {
      console.log('Starting payment deletion process for:', selectedPayments);
      
      // Check refund status for all selected payments
      const paymentsToCheck = filteredPayments.filter(p => 
        selectedPayments.includes(p.id) && 
        p.status === 'succeeded' && 
        !p.refund_id && 
        !refundStatuses[p.id]
      );

      console.log('Payments that need refund status check:', paymentsToCheck);

      for (const payment of paymentsToCheck) {
        const canDelete = await checkRefundStatus(payment);
        console.log(`Refund status check for payment ${payment.id}:`, canDelete);
        if (!canDelete) {
          throw new Error(`Payment ${payment.id} cannot be deleted as it hasn't been refunded`);
        }
      }

      if (selectedPayments.length === 1) {
        console.log('Deleting single payment:', selectedPayments[0]);
        await api.delete(`/admin/payments/${selectedPayments[0]}`);
      } else {
        console.log('Deleting multiple payments:', selectedPayments);
        await api.delete('/admin/payments', {
          data: { paymentIds: selectedPayments }
        });
      }
      setSuccess(`Successfully deleted ${selectedPayments.length} payment(s)`);
      setSelectedPayments([]);
      setDeletePaymentDialog(false);
      fetchPayments();
    } catch (err) {
      console.error('Error in payment deletion:', err);
      setError(err.response?.data?.error || err.message || 'Failed to delete payments');
    }
  };

  if (loading) {
    const spinner = (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
    return embedded ? spinner : <MainLayout>{spinner}</MainLayout>;
  }

  const handleStatusChange = async (registrationId, newStatus) => {
    try {
      await api.post(`/admin/booth-registrations/${registrationId}/status`, {
        status: newStatus
      });
      fetchIncompleteRegistrations(); // Refresh the list
      setSuccess('Registration status updated successfully');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update registration status');
    }
  };

  const showChrome = forcedTabIndex == null;

  const body = (
    <>
    <Container maxWidth="lg" sx={{ mt: embedded ? 0 : 4, mb: 4, px: embedded ? 0 : undefined }} disableGutters={embedded}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        {showChrome && (
          <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h4" component="h1">
              Admin Dashboard
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={autoRefresh}
                    onChange={(e) => setAutoRefresh(e.target.checked)}
                    color="primary"
                  />
                }
                label={
                  <Typography variant="body2" color="text.secondary">
                    Auto-refresh every 30s
                  </Typography>
                }
              />
              {activeTab === 0 && (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => setAddVendorDialog(true)}
                >
                  Add New Vendor
                </Button>
              )}
              {activeTab === 2 && isSuperAdmin && (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => setAddAdminDialog(true)}
                >
                  Add New Admin
                </Button>
              )}
              <Button
                variant="outlined"
                color="secondary"
                startIcon={<DescriptionIcon />}
                onClick={() => navigate('/admin/final-registrations')}
              >
                View Final Registration Status
              </Button>
              {isSuperAdmin && (
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() => navigate('/admin/festivals')}
                >
                  Manage Festivals
                </Button>
              )}
            </Box>
          </Box>
        )}

        <Paper sx={{ width: '100%', mb: 4 }} elevation={embedded ? 0 : 1} variant={embedded ? 'outlined' : 'elevation'}>
          {showChrome && (
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            indicatorColor="primary"
            textColor="primary"
            sx={{ 
              borderBottom: 1, 
              borderColor: 'divider',
              '& .MuiTab-root': {
                color: '#666',
                fontWeight: 500,
                opacity: 1,
                '&.Mui-selected': {
                  color: '#1976d2',
                  fontWeight: 'bold',
                  opacity: 1,
                },
              },
            }}
          >
            <Tab label="VENDORS" value={TAB_VENDORS} />
            <Tab label="BOOTHS" value={TAB_BOOTHS} />
            {isSuperAdmin && <Tab label="USERS" value={TAB_USERS} />}
            {isSuperAdmin && <Tab label="WEBHOOKS" value={TAB_WEBHOOKS} />}
            <Tab label="PAYMENTS" value={TAB_PAYMENTS} />
            <Tab label="PENDING APPROVALS" value={TAB_PENDING} />
            <Tab label="INCOMPLETE REGISTRATIONS" value={TAB_INCOMPLETE} />
            <Tab label="DUPLICATE USERS" value={TAB_DUPLICATES} />
          </Tabs>
          )}

          {/* Vendors Tab Panel */}
          {activeTab === 0 && (
            <Box sx={{ p: 3 }}>
              {selectedVendors.length > 0 && (
                <Box
                  sx={{
                    mb: 1.5,
                    px: 1.5,
                    py: 0.75,
                    borderRadius: 1,
                    bgcolor: 'action.hover',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    flexWrap: 'wrap',
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {selectedVendors.length} selected
                  </Typography>
                  <Box sx={{ flex: 1 }} />
                  <Button
                    size="small"
                    startIcon={<EmailIcon />}
                    onClick={handleOpenBulkVendorEmailDialog}
                    sx={{ textTransform: 'none' }}
                  >
                    Email
                  </Button>
                  <Button
                    size="small"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={() => setDeleteConfirmDialog(true)}
                    sx={{ textTransform: 'none' }}
                  >
                    Delete
                  </Button>
                  <Button
                    size="small"
                    onClick={() => setSelectedVendors([])}
                    sx={{ textTransform: 'none' }}
                  >
                    Clear
                  </Button>
                </Box>
              )}
              <Box
                sx={{
                  display: 'flex',
                  gap: 1,
                  mb: 2,
                  flexWrap: 'wrap',
                  alignItems: 'center',
                }}
              >
                <TextField
                  size="small"
                  label="Search"
                  value={vendorSearch}
                  onChange={e => setVendorSearch(e.target.value)}
                  placeholder="Business, Email, Booth..."
                  sx={{ minWidth: 240, flex: '1 1 240px', maxWidth: 360 }}
                />
                <Badge
                  color="primary"
                  badgeContent={activeVendorFilters.length}
                  overlap="rectangular"
                >
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<FilterListIcon />}
                    onClick={(e) => setFilterAnchorEl(e.currentTarget)}
                    aria-haspopup="true"
                    aria-expanded={Boolean(filterAnchorEl)}
                    sx={{ textTransform: 'none' }}
                  >
                    Filters
                  </Button>
                </Badge>
                {activeVendorFilters.map((f) => (
                  <Chip
                    key={f.key}
                    size="small"
                    label={`${f.label}: ${f.display[f.value] ?? f.value}`}
                    onDelete={f.reset}
                  />
                ))}
                {activeVendorFilters.length > 1 && (
                  <Button
                    size="small"
                    onClick={clearAllVendorFilters}
                    sx={{ textTransform: 'none' }}
                  >
                    Clear all
                  </Button>
                )}
                <Box sx={{ flex: 1 }} />
                <DisregardEmailButton />
                <Tooltip title="Export">
                  <IconButton
                    size="small"
                    onClick={(e) => setVendorExportAnchorEl(e.currentTarget)}
                    aria-haspopup="true"
                    aria-expanded={Boolean(vendorExportAnchorEl)}
                    aria-label="export vendors"
                  >
                    <FileDownloadIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Menu
                  anchorEl={vendorExportAnchorEl}
                  open={Boolean(vendorExportAnchorEl)}
                  onClose={() => setVendorExportAnchorEl(null)}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                >
                  <MenuItem
                    onClick={() => {
                      handleExportCSV(getVendorExportData(), 'vendors');
                      setVendorExportAnchorEl(null);
                    }}
                  >
                    Export as CSV
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      handleExportPDF(getVendorExportData(), 'Gingham Tree 2026 Vendors', [
                        { header: 'Business Name', field: 'Business Name' },
                        { header: 'Booth', field: 'Booth' },
                        { header: 'Phone', field: 'Phone' },
                        { header: 'Email', field: 'Email' },
                      ]);
                      setVendorExportAnchorEl(null);
                    }}
                  >
                    Export as PDF
                  </MenuItem>
                </Menu>
              </Box>
              <Popover
                open={Boolean(filterAnchorEl)}
                anchorEl={filterAnchorEl}
                onClose={() => setFilterAnchorEl(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
              >
                <Box sx={{ p: 2, width: 280, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  <FormControl size="small" fullWidth>
                    <InputLabel>Booth Assignment</InputLabel>
                    <Select
                      value={vendorAssignmentFilter}
                      label="Booth Assignment"
                      onChange={e => setVendorAssignmentFilter(e.target.value)}
                    >
                      <MenuItem value="all">All</MenuItem>
                      <MenuItem value="assigned">Assigned</MenuItem>
                      <MenuItem value="unassigned">Unassigned</MenuItem>
                    </Select>
                  </FormControl>
                  <FormControl size="small" fullWidth>
                    <InputLabel>Created By</InputLabel>
                    <Select
                      value={createdByFilter}
                      label="Created By"
                      onChange={e => setCreatedByFilter(e.target.value)}
                    >
                      <MenuItem value="all">All</MenuItem>
                      <MenuItem value="admin">Admin</MenuItem>
                      <MenuItem value="user">User</MenuItem>
                    </Select>
                  </FormControl>
                  <FormControl size="small" fullWidth>
                    <InputLabel>Payment Status</InputLabel>
                    <Select
                      value={vendorPaymentStatusFilter}
                      label="Payment Status"
                      onChange={e => setVendorPaymentStatusFilter(e.target.value)}
                    >
                      <MenuItem value="all">All</MenuItem>
                      <MenuItem value="pending">Pending</MenuItem>
                      <MenuItem value="succeeded">Completed</MenuItem>
                      <MenuItem value="failed">Failed</MenuItem>
                      <MenuItem value="canceled">Canceled</MenuItem>
                      <MenuItem value="no_payment">No payment</MenuItem>
                    </Select>
                  </FormControl>
                  <FormControl size="small" fullWidth>
                    <InputLabel>Registration Status</InputLabel>
                    <Select
                      value={vendorRegistrationStatusFilter}
                      label="Registration Status"
                      onChange={e => setVendorRegistrationStatusFilter(e.target.value)}
                    >
                      <MenuItem value="all">All</MenuItem>
                      <MenuItem value="pending_approval">Pending</MenuItem>
                      <MenuItem value="approved">Approved</MenuItem>
                      <MenuItem value="denied">Denied</MenuItem>
                      <MenuItem value="incomplete">Incomplete</MenuItem>
                      <MenuItem value="complete">Complete</MenuItem>
                    </Select>
                  </FormControl>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                    <Button
                      size="small"
                      onClick={clearAllVendorFilters}
                      disabled={activeVendorFilters.length === 0}
                      sx={{ textTransform: 'none' }}
                    >
                      Clear filters
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      onClick={() => setFilterAnchorEl(null)}
                      sx={{ textTransform: 'none' }}
                    >
                      Done
                    </Button>
                  </Box>
                </Box>
              </Popover>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox">
                        <Checkbox
                          indeterminate={selectedVendors.length > 0 && selectedVendors.length < vendors.length}
                          checked={vendors.length > 0 && selectedVendors.length === vendors.length}
                          onChange={handleSelectAllVendors}
                          color="primary"
                        />
                      </TableCell>
                      <TableCell>Vendor</TableCell>
                      <TableCell>Booth</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredVendors.map((vendor) => {
                      const { primary, secondary } = getVendorIdentityLines(vendor);
                      const contactEmail = getVendorContactEmail(vendor);

                      // Get overall registration status
                      const hasApprovedBooth = vendor.booths.some(b => b.registration_status === 'approved');
                      const hasPendingBooth = vendor.booths.some(b => b.registration_status === 'pending_approval');
                      const registrationStatus = hasApprovedBooth ? 'approved' : 
                                               hasPendingBooth ? 'pending_approval' : 
                                               vendor.registration_status || 'incomplete';

                      return (
                        <TableRow
                          key={vendor.id}
                          hover
                          selected={selectedVendors.includes(vendor.id)}
                        >
                          <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                            <Checkbox
                              checked={selectedVendors.includes(vendor.id)}
                              onChange={() => handleVendorSelect(vendor.id)}
                              color="primary"
                            />
                          </TableCell>
                          <TableCell>
                            <Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                <Typography
                                  variant="subtitle2"
                                  component="span"
                                  onClick={() => navigate(`/admin/vendors/${vendor.id}?returnTab=${activeTab}`)}
                                  sx={{
                                    color: 'primary.main',
                                    cursor: 'pointer',
                                    '&:hover': { textDecoration: 'underline' },
                                  }}
                                >
                                  {primary}
                                </Typography>
                                <CategoryChip
                                  categoryName={vendor.category_name}
                                  categorySlug={vendor.category_slug}
                                  categoryOtherDescription={vendor.category_other_description}
                                />
                              </Box>
                              {secondary ? (
                                <Typography variant="body2" color="text.secondary">
                                  {secondary}
                                </Typography>
                              ) : null}
                              {vendor.phone && (
                                <Typography variant="body2" color="text.secondary">{vendor.phone}</Typography>
                              )}
                              <Typography variant="body2" color="text.secondary">
                                {contactEmail || (isAdminPlaceholderVendor(vendor) ? 'No contact email' : '')}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <BoothChipList
                              booths={(vendor.booths || []).filter(b => b?.registration_status !== 'denied')}
                              emptyLabel={null}
                            />
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'flex-start' }}>
                              {(() => {
                                const label = registrationStatus === 'pending_approval'
                                  ? 'Pending'
                                  : (registrationStatus || '').replace(/_/g, ' ');
                                const color = registrationStatus === 'approved'
                                  ? 'success.main'
                                  : registrationStatus === 'pending_approval'
                                    ? 'warning.main'
                                    : registrationStatus === 'denied'
                                      ? 'error.main'
                                      : 'text.secondary';
                                const reasons = [];
                                if ((registrationStatus || '') === 'incomplete' && !isAdminPlaceholderVendor(vendor)) {
                                  if (!vendor.handmade_agreement_accepted) reasons.push('handmade');
                                  const hasAnyBooth = Array.isArray(vendor.booths) && vendor.booths.length > 0;
                                  if (!hasAnyBooth) reasons.push('no_booth');
                                  const hasPending = vendor.booths?.some(b => b.registration_status === 'pending_approval');
                                  if (!hasPending && hasAnyBooth) reasons.push('not_submitted');
                                }
                                return (
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                                    <Box sx={{
                                      display: 'inline-block',
                                      px: 1,
                                      py: 0.5,
                                      borderRadius: 1,
                                      border: '1px solid',
                                      borderColor: color,
                                      color: color,
                                      fontSize: '0.8125rem',
                                      textTransform: 'capitalize'
                                    }}>
                                      {label}
                                    </Box>
                                    {reasons.length > 0 && (
                                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                                        {reasons.includes('handmade') && (
                                          <Tooltip title="Handmade agreement not accepted">
                                            <IconButton size="small" onClick={() => openHelp('handmade')}>
                                              <DescriptionIcon fontSize="small" color="warning" />
                                            </IconButton>
                                          </Tooltip>
                                        )}
                                        {reasons.includes('no_booth') && (
                                          <Tooltip title="No booth selected">
                                            <IconButton size="small" onClick={() => openHelp('no_booth')}>
                                              <EventSeatIcon fontSize="small" color="warning" />
                                            </IconButton>
                                          </Tooltip>
                                        )}
                                        {reasons.includes('not_submitted') && (
                                          <Tooltip title="Not submitted for approval">
                                            <IconButton size="small" onClick={() => openHelp('not_submitted')}>
                                              <PendingActionsIcon fontSize="small" color="warning" />
                                            </IconButton>
                                          </Tooltip>
                                        )}
                                      </Box>
                                    )}
                                  </Box>
                                );
                              })()}
                              {(() => {
                                const raw = vendor.payment_status || '';
                                const label = raw === 'succeeded' ? 'Completed'
                                  : raw === 'pending' ? 'Pending'
                                  : raw === 'failed' ? 'Failed'
                                  : raw === 'canceled' ? 'Canceled'
                                  : 'No payment';
                                const color = raw === 'succeeded' ? 'success.main'
                                  : raw === 'pending' ? 'warning.main'
                                  : (raw === 'failed' || raw === 'canceled') ? 'error.main'
                                  : 'text.secondary';
                                return (
                                  <Box sx={{
                                    display: 'inline-block',
                                    px: 1,
                                    py: 0.5,
                                    borderRadius: 1,
                                    border: '1px solid',
                                    borderColor: color,
                                    color: color,
                                    fontSize: '0.8125rem',
                                    textTransform: 'capitalize'
                                  }}>
                                    {label}
                                  </Box>
                                );
                              })()}
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          {/* Booths Tab Panel */}
          {activeTab === 1 && (
            <Box sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                  <TextField
                    size="small"
                    placeholder="Search booths…"
                    value={boothSearch}
                    onChange={(e) => setBoothSearch(e.target.value)}
                    sx={{ minWidth: 240 }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <FormControl size="small" sx={{ minWidth: 150 }}>
                    <InputLabel>Filter Booths</InputLabel>
                    <Select
                      value={boothFilter}
                      label="Filter Booths"
                      onChange={(e) => setBoothFilter(e.target.value)}
                    >
                      <MenuItem value="all">All Booths</MenuItem>
                      <MenuItem value="assigned">Assigned Booths</MenuItem>
                      <MenuItem value="available">Available Booths</MenuItem>
                      <MenuItem value="unavailable">Unavailable Booths</MenuItem>
                    </Select>
                  </FormControl>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => setMapDialog(true)}
                    sx={{ minWidth: '120px' }}
                  >
                    View Map
                  </Button>
                  <Button
                    variant="contained"
                    color="secondary"
                    startIcon={<RefreshIcon />}
                    onClick={handleRefresh}
                    sx={{ minWidth: '120px' }}
                  >
                    Refresh
                  </Button>
                </Box>
              </Box>
              <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<FileDownloadIcon />}
                  onClick={() => handleExportCSV(getBoothExportData(), 'booths')}
                  sx={{ minWidth: '120px' }}
                >
                  Export CSV
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<FileDownloadIcon />}
                  onClick={() => handleExportPDF(getBoothExportData(), 'Booths', [
                    { header: 'Booth Number', field: 'Booth Number' },
                    { header: 'Status', field: 'Status' },
                    { header: 'Assigned Vendor', field: 'Assigned Vendor' },
                    { header: 'Has Electricity', field: 'Has Electricity' },
                    { header: 'Section', field: 'Section' },
                    { header: 'Is Corner', field: 'Is Corner' }
                  ])}
                  sx={{ minWidth: '120px' }}
                >
                  Export PDF
                </Button>
              </Box>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Booth Number</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Assigned Vendor</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {booths
                      .filter(booth => booth.cell_type === 'booth')
                      .filter(booth => {
                        if (boothFilter === 'all') return true;
                        if (boothFilter === 'assigned') return booth.is_registered;
                        if (boothFilter === 'available') return !booth.is_registered && booth.booth_status !== 'unavailable';
                        if (boothFilter === 'unavailable') return booth.booth_status === 'unavailable';
                        return true;
                      })
                      .filter(booth => matchesSearchFields(booth, boothSearch, [
                        'cell_name',
                        'section',
                        'vendor_name',
                        'booth_status',
                        'registration_status',
                      ]))
                      .sort((a, b) => {
                        if (!a.cell_name) return 1;
                        if (!b.cell_name) return -1;
                        return a.cell_name.localeCompare(b.cell_name);
                      })
                      .map((booth) => (
                        <TableRow key={booth.id}>
                          <TableCell>{booth.cell_name}</TableCell>
                          <TableCell>
                            {booth.booth_status === 'unavailable' ? 'Unavailable' : 
                             booth.is_registered ? 'Assigned' : 'Available'}
                          </TableCell>
                          <TableCell>{booth.vendor_name || '-'}</TableCell>
                          <TableCell>
                            {booth.is_registered ? (
                              <Tooltip title="Remove Assignment">
                                <IconButton
                                  onClick={() => handleRemoveBoothAssignment(booth.id)}
                                  color="error"
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Tooltip>
                            ) : (
                              <Tooltip title={booth.booth_status === 'unavailable' ? 'Mark as Available' : 'Mark as Unavailable'}>
                                <IconButton
                                  onClick={() => {
                                    setSelectedCell(booth);
                                    setEditData({
                                      ...editData,
                                      is_available: booth.booth_status !== 'unavailable'
                                    });
                                    setEditDialog(true);
                                  }}
                                  color={booth.booth_status === 'unavailable' ? 'success' : 'warning'}
                                >
                                  {booth.booth_status === 'unavailable' ? <EditIcon /> : <CloseIcon />}
                                </IconButton>
                              </Tooltip>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          {/* Users Tab Panel */}
          {activeTab === 2 && isSuperAdmin && (
            <Box sx={{ p: 3 }}>
              <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  <Typography variant="subtitle1">
                    {selectedUsers.length} user(s) selected
                  </Typography>
                  <Button
                    variant="contained"
                    color="error"
                    disabled={selectedUsers.length === 0}
                    onClick={handleDeleteUsers}
                    sx={{ minWidth: '120px' }}
                  >
                    Delete Selected
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<EmailIcon />}
                    disabled={selectedUsers.length === 0}
                    onClick={handleOpenBulkVendorEmailDialog}
                    sx={{ minWidth: '120px' }}
                  >
                    Send Email
                  </Button>
                  <Button
                    variant="contained"
                    color="secondary"
                    startIcon={<RefreshIcon />}
                    onClick={handleRefresh}
                    sx={{ minWidth: '120px' }}
                  >
                    Refresh
                  </Button>
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<AddIcon />}
                    onClick={async () => {
                      await fetchVendorsNeedingUsers();
                      setAddUserDialog(true);
                    }}
                    sx={{ minWidth: '120px' }}
                  >
                    Add User
                  </Button>
                </Box>
              </Box>
              <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<FileDownloadIcon />}
                  onClick={() => handleExportCSV(getUserExportData(), 'users')}
                  sx={{ minWidth: '120px' }}
                >
                  Export CSV
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<FileDownloadIcon />}
                  onClick={() => handleExportPDF(getUserExportData(), 'Users', [
                    { header: 'Name', field: 'Name' },
                    { header: 'Email', field: 'Email' },
                    { header: 'Role', field: 'Role' },
                    { header: 'Created At', field: 'Created At' }
                  ])}
                  sx={{ minWidth: '120px' }}
                >
                  Export PDF
                </Button>
              </Box>
              <Box sx={{ mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                <TextField
                  size="small"
                  placeholder="Search users…"
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                  sx={{ minWidth: 260 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel>Filter by Role</InputLabel>
                  <Select
                    value={roleFilter}
                    label="Filter by Role"
                    onChange={(e) => setRoleFilter(e.target.value)}
                  >
                    <MenuItem value="all">All Users</MenuItem>
                    <MenuItem value="superadmin">Super Admins</MenuItem>
                    <MenuItem value="admin">Admins</MenuItem>
                    <MenuItem value="vendor">Vendors</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox">
                        <Checkbox
                          indeterminate={selectedUsers.length > 0 && selectedUsers.length < users.length}
                          checked={users.length > 0 && selectedUsers.length === users.length}
                          onChange={handleSelectAllUsers}
                          color="primary"
                        />
                      </TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Role</TableCell>
                      <TableCell>Created At</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {users
                      .filter(user => roleFilter === 'all' || user.role === roleFilter)
                      .filter((user) => matchesSearchFields(user, userSearch, [
                        'email',
                        'firstName',
                        'lastName',
                        (u) => `${u.firstName || ''} ${u.lastName || ''}`.trim(),
                        'role',
                      ]))
                      .map((user) => (
                        <TableRow 
                          key={user.id}
                          selected={selectedUsers.includes(user.id)}
                        >
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={selectedUsers.includes(user.id)}
                              onChange={() => handleUserSelect(user.id)}
                              color="primary"
                            />
                          </TableCell>
                          <TableCell>{`${user.firstName} ${user.lastName}`}</TableCell>
                          <TableCell>{user.email}</TableCell>
                          <TableCell>
                            <FormControl size="small" sx={{ minWidth: 140 }}>
                              <Select
                                value={user.role}
                                onChange={(e) => handleRoleChange(user.id, e.target.value)}
                                size="small"
                              >
                                <MenuItem value="superadmin">Super Admin</MenuItem>
                                <MenuItem value="admin">Admin</MenuItem>
                                <MenuItem value="vendor">Vendor</MenuItem>
                              </Select>
                            </FormControl>
                          </TableCell>
                          <TableCell>{new Date(user.createdAt).toLocaleDateString()}</TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <Tooltip title="Edit User">
                                <IconButton onClick={() => handleEditUser(user)}>
                                  <EditIcon />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Reset Password">
                                <IconButton 
                                  onClick={() => {
                                    setSelectedUser(user);
                                    setResetPasswordDialog(true);
                                  }}
                                >
                                  <LockIcon />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Send Email">
                                <IconButton onClick={() => handleOpenSingleVendorEmailDialog(user)}>
                                  <EmailIcon />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          {/* Webhooks Tab Panel */}
          {activeTab === 3 && isSuperAdmin && (
            <Box sx={{ p: 3 }}>
              <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  <Typography variant="subtitle1">
                    {selectedWebhooks.length} webhook event(s) selected
                  </Typography>
                  <Button
                    variant="contained"
                    color="error"
                    disabled={selectedWebhooks.length === 0}
                    onClick={() => setDeleteWebhookDialog(true)}
                    sx={{ minWidth: '120px' }}
                  >
                    Delete Selected
                  </Button>
                  <Button
                    variant="contained"
                    color="secondary"
                    startIcon={<RefreshIcon />}
                    onClick={handleRefresh}
                    sx={{ minWidth: '120px' }}
                  >
                    Refresh
                  </Button>
                </Box>
              </Box>

              <Box sx={{ mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                <TextField
                  size="small"
                  placeholder="Search recipient, message ID…"
                  value={webhookSearch}
                  onChange={(e) => {
                    setWebhookSearch(e.target.value);
                    setWebhookPage(0);
                  }}
                  sx={{ minWidth: 280 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel>Event Type</InputLabel>
                  <Select
                    value={webhookEventType}
                    label="Event Type"
                    onChange={(e) => {
                      setWebhookEventType(e.target.value);
                      setWebhookPage(0);
                    }}
                  >
                    <MenuItem value="all">All Events</MenuItem>
                    <MenuItem value="Delivered">Delivered</MenuItem>
                    <MenuItem value="Bounce">Bounce</MenuItem>
                    <MenuItem value="SpamComplaint">Spam Complaint</MenuItem>
                    <MenuItem value="Open">Open</MenuItem>
                    <MenuItem value="Click">Click</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox">
                        <Checkbox
                          indeterminate={selectedWebhooks.length > 0 && selectedWebhooks.length < webhookEvents.length}
                          checked={webhookEvents.length > 0 && selectedWebhooks.length === webhookEvents.length}
                          onChange={handleSelectAllWebhooks}
                          color="primary"
                        />
                      </TableCell>
                      <TableCell>Event Type</TableCell>
                      <TableCell>Message ID</TableCell>
                      <TableCell>Recipient</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {webhookEvents.map((event) => (
                      <TableRow 
                        key={event.id}
                        selected={selectedWebhooks.includes(event.id)}
                      >
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={selectedWebhooks.includes(event.id)}
                            onChange={() => handleWebhookSelect(event.id)}
                            color="primary"
                          />
                        </TableCell>
                        <TableCell>{event.event_type}</TableCell>
                        <TableCell>{event.message_id}</TableCell>
                        <TableCell>{event.recipient}</TableCell>
                        <TableCell>
                          {new Date(event.created_at).toLocaleString()}
                        </TableCell>
                        <TableCell>
                          <Tooltip title="View Details">
                            <IconButton
                              onClick={() => {
                                setSelectedVendorDetails({
                                  ...event,
                                  business_name: 'Webhook Event Details'
                                });
                                setViewDetailsDialog(true);
                              }}
                            >
                              <VisibilityIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <TablePagination
                component="div"
                count={webhookTotalCount}
                page={webhookPage}
                onPageChange={(e, newPage) => setWebhookPage(newPage)}
                rowsPerPage={webhookRowsPerPage}
                onRowsPerPageChange={(e) => {
                  setWebhookRowsPerPage(parseInt(e.target.value, 10));
                  setWebhookPage(0);
                }}
              />
            </Box>
          )}

          {/* Payments Tab Panel */}
          {activeTab === 4 && (
            <Box sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>Payments</Typography>
              {selectedPayments.length > 0 && (
                <Box
                  sx={{
                    mb: 1.5,
                    px: 1.5,
                    py: 0.75,
                    borderRadius: 1,
                    bgcolor: 'action.hover',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    flexWrap: 'wrap',
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {selectedPayments.length} selected
                  </Typography>
                  <Box sx={{ flex: 1 }} />
                  <Button
                    size="small"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={() => setDeletePaymentDialog(true)}
                    sx={{ textTransform: 'none' }}
                  >
                    Delete
                  </Button>
                  <Button
                    size="small"
                    onClick={() => setSelectedPayments([])}
                    sx={{ textTransform: 'none' }}
                  >
                    Clear
                  </Button>
                </Box>
              )}
              <Box
                sx={{
                  display: 'flex',
                  gap: 1,
                  mb: 2,
                  flexWrap: 'wrap',
                  alignItems: 'center',
                }}
              >
                <TextField
                  size="small"
                  label="Search"
                  value={paymentSearch}
                  onChange={e => setPaymentSearch(e.target.value)}
                  placeholder="Vendor, Email, Booth, Payment ID..."
                  sx={{ minWidth: 240, flex: '1 1 240px', maxWidth: 360 }}
                />
                <Badge
                  color="primary"
                  badgeContent={activePaymentFilters.length}
                  overlap="rectangular"
                >
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<FilterListIcon />}
                    onClick={(e) => setPaymentFilterAnchorEl(e.currentTarget)}
                    aria-haspopup="true"
                    aria-expanded={Boolean(paymentFilterAnchorEl)}
                    sx={{ textTransform: 'none' }}
                  >
                    Filters
                  </Button>
                </Badge>
                {activePaymentFilters.map((f) => (
                  <Chip
                    key={f.key}
                    size="small"
                    label={`${f.label}: ${f.display[f.value] ?? f.value}`}
                    onDelete={f.reset}
                  />
                ))}
                {activePaymentFilters.length > 1 && (
                  <Button
                    size="small"
                    onClick={clearAllPaymentFilters}
                    sx={{ textTransform: 'none' }}
                  >
                    Clear all
                  </Button>
                )}
                <Box sx={{ flex: 1 }} />
                <Tooltip title="Re-check refund status against Stripe">
                  <span>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={checkAllRefundStatuses}
                      disabled={refundCheckLoading}
                      startIcon={refundCheckLoading ? <CircularProgress size={16} /> : <RefreshIcon />}
                      sx={{ textTransform: 'none' }}
                    >
                      Check refunds
                    </Button>
                  </span>
                </Tooltip>
              </Box>
              <Popover
                open={Boolean(paymentFilterAnchorEl)}
                anchorEl={paymentFilterAnchorEl}
                onClose={() => setPaymentFilterAnchorEl(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
              >
                <Box sx={{ p: 2, width: 280, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  <FormControl size="small" fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={paymentStatusFilter}
                      label="Status"
                      onChange={e => setPaymentStatusFilter(e.target.value)}
                    >
                      <MenuItem value="all">All Statuses</MenuItem>
                      <MenuItem value="pending">Pending</MenuItem>
                      <MenuItem value="succeeded">Succeeded</MenuItem>
                      <MenuItem value="failed">Failed</MenuItem>
                      <MenuItem value="canceled">Canceled</MenuItem>
                    </Select>
                  </FormControl>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                    <Button
                      size="small"
                      onClick={clearAllPaymentFilters}
                      disabled={activePaymentFilters.length === 0}
                      sx={{ textTransform: 'none' }}
                    >
                      Clear filters
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      onClick={() => setPaymentFilterAnchorEl(null)}
                      sx={{ textTransform: 'none' }}
                    >
                      Done
                    </Button>
                  </Box>
                </Box>
              </Popover>

              {/* Update the table to show refund status */}
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox">
                        <Checkbox
                          indeterminate={selectedPayments.length > 0 && selectedPayments.length < filteredPayments.length}
                          checked={filteredPayments.length > 0 && selectedPayments.length === filteredPayments.length}
                          onChange={handleSelectAllPayments}
                          color="primary"
                        />
                      </TableCell>
                      {PAYMENT_COLUMNS.map(col => (
                        <TableCell key={col.field}>{col.header}</TableCell>
                      ))}
                      <TableCell>Refund Status</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredPayments.map((payment) => {
                      const canDelete = payment.status !== 'succeeded' || payment.refund_id || refundStatuses[payment.id];
                      
                      return (
                        <TableRow 
                          key={payment.id}
                          hover
                          selected={selectedPayments.includes(payment.id)}
                        >
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={selectedPayments.includes(payment.id)}
                              onChange={() => handlePaymentSelect(payment.id)}
                              color="primary"
                              disabled={!canDelete}
                            />
                          </TableCell>
                          <TableCell>{payment.business_name}</TableCell>
                          <TableCell>{payment.email || '-'}</TableCell>
                          <TableCell>{payment.booth_number}</TableCell>
                          <TableCell>{payment.status}</TableCell>
                          <TableCell>{payment.stripe_payment_intent_id || '-'}</TableCell>
                          <TableCell>
                            {payment.status === 'succeeded' ? (
                              payment.refund_id ? (
                                <Typography color="success.main">Refunded</Typography>
                              ) : refundStatuses[payment.id] ? (
                                <Typography color="success.main">Refunded (Stripe)</Typography>
                              ) : (
                                <Typography color="warning.main">Not Refunded</Typography>
                              )
                            ) : (
                              <Typography color="text.secondary">N/A</Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <Tooltip title="View Details">
                                <IconButton onClick={() => navigate(`/admin/payments/${payment.id}?returnTab=${activeTab}`)}>
                                  <VisibilityIcon />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title={!canDelete ? "Cannot delete succeeded payment that hasn't been refunded" : "Delete"}>
                                <span>
                                  <IconButton 
                                    onClick={() => {
                                      setSelectedPayments([payment.id]);
                                      setDeletePaymentDialog(true);
                                    }}
                                    disabled={!canDelete}
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          {/* Pending Approvals Tab Panel */}
          {activeTab === 5 && (
            <Box sx={{ p: 3 }}>
              <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6">
                  Pending Vendor Approvals ({pendingVendors.length})
                </Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Button
                    variant="contained"
                    color="primary"
                    disabled={selectedPendingVendors.length === 0}
                    onClick={() => setBulkApprovalDialog(true)}
                    sx={{ minWidth: '120px' }}
                  >
                    Bulk Approve ({selectedPendingVendors.length})
                  </Button>
                  <Button
                    variant="contained"
                    color="secondary"
                    startIcon={<RefreshIcon />}
                    onClick={fetchPendingVendors}
                    sx={{ minWidth: '120px' }}
                  >
                    Refresh
                  </Button>
                </Box>
              </Box>

              {pendingLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                  <CircularProgress />
                </Box>
              ) : pendingVendors.length === 0 ? (
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    No Pending Approvals
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    All vendor registrations have been reviewed. New submissions will appear here.
                  </Typography>
                </Paper>
              ) : (
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell padding="checkbox">
                          <Checkbox
                            indeterminate={selectedPendingVendors.length > 0 && selectedPendingVendors.length < pendingVendors.length}
                            checked={pendingVendors.length > 0 && selectedPendingVendors.length === pendingVendors.length}
                            onChange={handleSelectAllPendingVendors}
                            color="primary"
                          />
                        </TableCell>
                        <TableCell>Business Name</TableCell>
                        <TableCell>Contact</TableCell>
                        <TableCell>Booth</TableCell>
                        <TableCell>Submitted</TableCell>
                        <TableCell align="center">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {pendingVendors.map((vendor) => (
                        <TableRow key={vendor.vendor_id} selected={selectedPendingVendors.includes(vendor.vendor_id)}>
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={selectedPendingVendors.includes(vendor.vendor_id)}
                              onChange={() => handlePendingVendorSelect(vendor.vendor_id)}
                              color="primary"
                            />
                          </TableCell>
                          <TableCell>
                            <Box>
                              {(() => {
                                const { primary, secondary } = getVendorIdentityLines(vendor);
                                return (
                                  <>
                                    <Typography variant="subtitle2">{primary}</Typography>
                                    {secondary ? (
                                      <Typography variant="body2" color="text.secondary">
                                        {secondary}
                                      </Typography>
                                    ) : null}
                                  </>
                                );
                              })()}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Box>
                              <Typography variant="body2">{getVendorContactEmail(vendor) || '—'}</Typography>
                              <Typography variant="body2" color="text.secondary">
                                {vendor.phone}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            {vendor.booth_name ? (
                              <Box
                                sx={{ cursor: 'pointer' }}
                                onClick={() => {
                                  setSelectedVendorForBooth(vendor);
                                  setBoothSelectionDialog(true);
                                }}
                              >
                                <BoothChip
                                  name={vendor.booth_name}
                                  isCorner={vendor.is_corner}
                                  needsElectricity={vendor.needs_electricity}
                                />
                              </Box>
                            ) : (
                              <Typography
                                variant="body2"
                                sx={{
                                  cursor: 'pointer',
                                  color: 'primary.main',
                                  '&:hover': { textDecoration: 'underline' },
                                }}
                                onClick={() => {
                                  setSelectedVendorForBooth(vendor);
                                  setBoothSelectionDialog(true);
                                }}
                              >
                                Click to assign booth
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {new Date(vendor.created_at).toLocaleDateString()}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
                              <Button
                                variant="contained"
                                color="success"
                                size="small"
                                onClick={() => {
                                  setSelectedPendingVendor(vendor);
                                  setApprovalDialog(true);
                                }}
                              >
                                Approve
                              </Button>
                              <Button
                                variant="contained"
                                color="error"
                                size="small"
                                onClick={() => {
                                  setSelectedPendingVendor(vendor);
                                  setDenialDialog(true);
                                }}
                              >
                                Deny
                              </Button>
                              <Tooltip title="Reset to incomplete - removes booth assignment but keeps vendor account">
                                <Button
                                  variant="outlined"
                                  color="warning"
                                  size="small"
                                  onClick={() => handleResetVendorToIncomplete(vendor.vendor_id)}
                                >
                                  Reset
                                </Button>
                              </Tooltip>
                              <Tooltip title="View Details">
                                <IconButton onClick={() => navigate(`/admin/vendors/${vendor.vendor_id}?returnTab=${activeTab}`)}>
                                  <VisibilityIcon />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          )}

          {/* Incomplete Registrations Tab Panel */}
          {activeTab === 6 && (
            <>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Incomplete Registrations
              </Typography>
              {incompleteLoading ? (
                <CircularProgress />
              ) : (
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Vendor</TableCell>
                        <TableCell>Booth</TableCell>
                        <TableCell>Contact</TableCell>
                        <TableCell>Registration Date</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {incompleteRegistrations.map((reg) => (
                        <TableRow key={reg.registration_id}>
                          <TableCell>
                            {(() => {
                              const { primary, secondary } = getVendorIdentityLines(reg);
                              return (
                                <>
                                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    {primary}
                                  </Typography>
                                  {secondary ? (
                                    <Typography variant="caption" color="text.secondary" display="block">
                                      {secondary}
                                    </Typography>
                                  ) : null}
                                </>
                              );
                            })()}
                          </TableCell>
                          <TableCell>
                            {reg.booth_name ? (
                              <BoothChip
                                name={reg.booth_name}
                                isCorner={reg.is_corner}
                                needsElectricity={reg.needs_electricity}
                              />
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                No booth
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {getVendorContactEmail(reg) || '—'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {new Date(reg.registration_date).toLocaleDateString()}
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <StyledButton
                                size="small"
                                onClick={() => handleStatusChange(reg.registration_id, 'pending_approval')}
                              >
                                Mark Pending
                              </StyledButton>
                              <StyledButton
                                size="small"
                                variant="contained"
                                onClick={() => handleStatusChange(reg.registration_id, 'approved')}
                              >
                                Approve
                              </StyledButton>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </>
          )}

          {/* Duplicate Users Tab Panel */}
          {activeTab === 7 && (
            <Box sx={{ p: 3 }}>
              <DuplicateUserManager />
            </Box>
          )}
        </Paper>

        {renderAddVendorDialog()}
        {renderEditVendorDialog()}

        {/* Map Dialog */}
        <Dialog
          fullScreen
          open={mapDialog}
          onClose={() => setMapDialog(false)}
          TransitionComponent={Transition}
        >
          <AppBar sx={{ position: 'relative', background: '#fafbfc', color: '#222', boxShadow: 0 }}>
            <Toolbar>
              <Typography sx={{ flex: 1 }} variant="h6" component="div">
                Booth Map
              </Typography>
              <Box 
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 0.5, 
                  mr: 2,
                  background: '#fff',
                  borderRadius: 1,
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  p: 0.5,
                }}
              >
                <Button
                  variant="text"
                  size="small"
                  onClick={handleZoomOut}
                  disabled={zoom <= 0.5}
                  sx={{
                    minWidth: 32,
                    height: 32,
                    borderRadius: 1,
                    color: '#666',
                    '&:hover': {
                      background: '#f5f5f5',
                    },
                    '&.Mui-disabled': {
                      color: '#ccc',
                    },
                  }}
                >
                  −
                </Button>
                <Button
                  variant="text"
                  size="small"
                  onClick={handleResetZoom}
                  sx={{
                    minWidth: 64,
                    height: 32,
                    borderRadius: 1,
                    color: '#666',
                    fontSize: '0.875rem',
                    '&:hover': {
                      background: '#f5f5f5',
                    },
                  }}
                >
                  {Math.round(zoom * 100)}%
                </Button>
                <Button
                  variant="text"
                  size="small"
                  onClick={handleZoomIn}
                  disabled={zoom >= 2}
                  sx={{
                    minWidth: 32,
                    height: 32,
                    borderRadius: 1,
                    color: '#666',
                    '&:hover': {
                      background: '#f5f5f5',
                    },
                    '&.Mui-disabled': {
                      color: '#ccc',
                    },
                  }}
                >
                  +
                </Button>
              </Box>
              <IconButton
                edge="end"
                color="inherit"
                onClick={() => setMapDialog(false)}
                aria-label="close"
              >
                <CloseIcon />
              </IconButton>
            </Toolbar>
          </AppBar>
          <Box sx={{ p: 2, flex: 1, overflow: 'auto', background: '#f5f6fa', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-start' }}>
            <Box sx={{ position: 'relative' }}>
              <Box 
                sx={{ 
                  background: '#f5f6fa', 
                  mx: 'auto', 
                  p: 2,
                  transform: `scale(${zoom})`,
                  transformOrigin: 'top center',
                  transition: 'transform 0.2s ease-in-out',
                }}
              >
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: `repeat(${width}, ${BASE_CELL_SIZE}px)`,
                    gridTemplateRows: `repeat(${height}, ${BASE_CELL_SIZE}px)`,
                    gap: '1px',
                    background: '#888',
                    justifyContent: 'center',
                    userSelect: 'none',
                  }}
                >
                  {grid.flat().map((cell, i) => (
                    <Box
                      key={i}
                      sx={{
                        width: BASE_CELL_SIZE,
                        height: BASE_CELL_SIZE,
                        background: getCellColor(cell),
                        boxSizing: 'border-box',
                        cursor: cell?.cell_type === 'booth' ? 'pointer' : 'default',
                        transition: 'box-shadow 0.2s, border 0.2s',
                        border: 'none',
                        position: 'relative',
                        '&:hover': {
                          boxShadow: cell?.cell_type === 'booth' ? '0 0 0 1px #bbb' : 'none',
                          zIndex: 1,
                        },
                      }}
                      onClick={() => handleCellClick(cell)}
                      title={cell?.cell_name || cell?.cell_type || 'empty'}
                    >
                      {cell?.cell_name && cell?.cell_type === 'booth' && (
                        <Typography
                          sx={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            fontSize: '12px',
                            lineHeight: 1,
                            color: '#fff',
                            textAlign: 'center',
                            width: '100%',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            pointerEvents: 'none',
                          }}
                        >
                          {cell.cell_name}
                        </Typography>
                      )}
                    </Box>
                  ))}
                </Box>
              </Box>
            </Box>
          </Box>
        </Dialog>

        {/* Edit Booth Dialog */}
        <Dialog
          open={editDialog}
          onClose={() => setEditDialog(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            Edit Booth {selectedCell?.cell_name}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={editData.is_available}
                    onChange={(e) => setEditData(prev => ({
                      ...prev,
                      is_available: e.target.checked
                    }))}
                  />
                }
                label="Available for Assignment"
                sx={{ mb: 2 }}
              />
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {selectedCell?.vendor_name ? `Currently assigned to: ${selectedCell.vendor_name}` : 'Not currently assigned'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Current Status: {selectedCell?.booth_status || 'available'}
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditDialog(false)}>Cancel</Button>
            <Button onClick={handleEditSubmit} variant="contained">
              Save Changes
            </Button>
          </DialogActions>
        </Dialog>

        {/* Add Admin Dialog */}
        <Dialog 
          open={addAdminDialog} 
          onClose={() => setAddAdminDialog(false)} 
          maxWidth="sm" 
          fullWidth
        >
          <DialogTitle>
            <Typography variant="h6" component="div">
              Add New Admin User
            </Typography>
          </DialogTitle>
          <DialogContent dividers>
            <Box sx={{ mb: 3 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="First Name"
                    name="firstName"
                    value={newAdminData.firstName}
                    onChange={(e) => setNewAdminData({ ...newAdminData, firstName: e.target.value })}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Last Name"
                    name="lastName"
                    value={newAdminData.lastName}
                    onChange={(e) => setNewAdminData({ ...newAdminData, lastName: e.target.value })}
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Email"
                    name="email"
                    type="email"
                    value={newAdminData.email}
                    onChange={(e) => setNewAdminData({ ...newAdminData, email: e.target.value })}
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Password"
                    name="password"
                    type="password"
                    value={newAdminData.password}
                    onChange={(e) => setNewAdminData({ ...newAdminData, password: e.target.value })}
                    required
                  />
                </Grid>
              </Grid>
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button 
              onClick={() => setAddAdminDialog(false)}
              sx={{ mr: 1 }}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleAddAdmin} 
              variant="contained" 
              color="primary"
            >
              Add Admin
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteConfirmDialog}
          onClose={() => setDeleteConfirmDialog(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            {activeTab === 0 ? 'Confirm Vendor Deletion' : 'Confirm User Deletion'}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle1" color="error" gutterBottom>
                Warning: This action cannot be undone
              </Typography>
              <Typography variant="body1" paragraph>
                {activeTab === 0 ? 
                  `You are about to delete ${deleteDetails.vendorCount} vendor(s). This will:` :
                  `You are about to delete ${deleteDetails.userCount} user(s). This will:`
                }
              </Typography>
              <Box component="ul" sx={{ pl: 2 }}>
                {activeTab === 0 ? (
                  <>
                    <Typography component="li">
                      Delete {deleteDetails.vendorCount} vendor profile(s)
                    </Typography>
                    <Typography component="li">
                      Remove {deleteDetails.boothRegistrations} booth registration(s)
                    </Typography>
                    <Typography component="li">
                      Note: {deleteDetails.userAccounts} associated user account(s) will be retained for historical purposes
                    </Typography>
                  </>
                ) : (
                  <>
                    <Typography component="li">
                      Delete {deleteDetails.vendorCount} vendor user(s)
                    </Typography>
                    <Typography component="li">
                      Delete {deleteDetails.adminCount} admin user(s)
                    </Typography>
                    <Typography component="li">
                      Remove {deleteDetails.boothRegistrations} booth registration(s)
                    </Typography>
                  </>
                )}
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Are you sure you want to proceed with this deletion?
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteConfirmDialog(false)}>Cancel</Button>
            <Button 
              onClick={activeTab === 0 ? confirmDeleteVendors : confirmDeleteUsers} 
              color="error" 
              variant="contained"
            >
              {activeTab === 0 ? 'Delete Vendors' : 'Delete Users'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Email Dialog */}
        <Dialog open={emailDialogOpen} onClose={() => setEmailDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Send Email</DialogTitle>
          <DialogContent>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              To: {emailDialogRecipients.join(', ')}
            </Typography>
            <TextField
              label="Subject"
              fullWidth
              value={emailSubject}
              onChange={e => setEmailSubject(e.target.value)}
              sx={{ mb: 2 }}
            />
            <TextField
              label="Message"
              fullWidth
              multiline
              minRows={5}
              value={emailMessage}
              onChange={e => setEmailMessage(e.target.value)}
              sx={{ mb: 2 }}
            />
            {emailResults && (
              <Box sx={{ mb: 2 }}>
                {emailResults.map((result, idx) => (
                  <Alert key={idx} severity={result.success ? 'success' : 'error'}>
                    {result.email}: {result.success ? 'Sent' : `Failed - ${result.error}`}
                  </Alert>
                ))}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEmailDialogOpen(false)} disabled={emailSending}>Cancel</Button>
            <Button onClick={handleSendEmail} variant="contained" color="primary" disabled={emailSending || !emailSubject || !emailMessage}>
              {emailSending ? 'Sending...' : 'Send'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Add User Dialog */}
        <Dialog 
          open={addUserDialog} 
          onClose={() => setAddUserDialog(false)} 
          maxWidth="sm" 
          fullWidth
        >
          <DialogTitle>
            <Typography variant="h6" component="div">
              Add New User
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Create a new user account and send an email invitation to set up their password
            </Typography>
          </DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2 }}>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Link to Existing Vendor (Optional)</InputLabel>
                <Select
                  value={newUserData.vendorId}
                  onChange={(e) => setNewUserData({ ...newUserData, vendorId: e.target.value })}
                  label="Link to Existing Vendor (Optional)"
                >
                  <MenuItem value="">
                    <em>Create standalone user account</em>
                  </MenuItem>
                  {vendorsNeedingUsers.map((vendor) => (
                    <MenuItem key={vendor.id} value={vendor.id}>
                      {vendor.business_name}
                      <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                        (Created: {new Date(vendor.created_at).toLocaleDateString()})
                      </Typography>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              
              {newUserData.vendorId && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  This will create a user account for the selected vendor and send them login credentials.
                </Alert>
              )}

              <TextField
                label="Email Address"
                type="email"
                fullWidth
                value={newUserData.email}
                onChange={(e) => setNewUserData({ ...newUserData, email: e.target.value })}
                sx={{ mb: 2 }}
                required
              />
              <TextField
                label="First Name"
                fullWidth
                value={newUserData.firstName}
                onChange={(e) => setNewUserData({ ...newUserData, firstName: e.target.value })}
                sx={{ mb: 2 }}
                required
              />
              <TextField
                label="Last Name"
                fullWidth
                value={newUserData.lastName}
                onChange={(e) => setNewUserData({ ...newUserData, lastName: e.target.value })}
                sx={{ mb: 2 }}
                required
              />
              <Alert severity="info" sx={{ mt: 2 }}>
                An email invitation will be sent to the user with instructions to set up their password.
              </Alert>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={() => {
                setAddUserDialog(false);
                setNewUserData({
                  email: '',
                  firstName: '',
                  lastName: '',
                  vendorId: '',
                });
              }}
              sx={{ mr: 1 }}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleAddUser} 
              variant="contained" 
              color="primary"
            >
              Send Invitation
            </Button>
          </DialogActions>
        </Dialog>

        {/* Vendor Approval Dialog */}
        <Dialog open={approvalDialog} onClose={() => setApprovalDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Approve Vendor Registration</DialogTitle>
          <DialogContent>
            {selectedPendingVendor && (
              <Box>
                <Typography variant="h6" gutterBottom>
                  {selectedPendingVendor.business_name}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {selectedPendingVendor.firstName} {selectedPendingVendor.lastName} - {selectedPendingVendor.email}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, mb: 1 }}>
                  <Typography variant="body2">Booth:</Typography>
                  {selectedPendingVendor.booth_name ? (
                    <BoothChip
                      name={selectedPendingVendor.booth_name}
                      isCorner={selectedPendingVendor.is_corner}
                      needsElectricity={selectedPendingVendor.needs_electricity}
                    />
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No booth selected
                    </Typography>
                  )}
                </Box>
                <TextField
                  label="Approval Notes (Optional)"
                  fullWidth
                  multiline
                  rows={3}
                  value={approvalNotes}
                  onChange={e => setApprovalNotes(e.target.value)}
                  sx={{ mt: 2 }}
                  placeholder="Add any notes for the vendor approval..."
                />
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setApprovalDialog(false)}>Cancel</Button>
            <Button 
              onClick={() => handleApproveVendor(selectedPendingVendor?.vendor_id)} 
              variant="contained" 
              color="success"
            >
              Approve Vendor
            </Button>
          </DialogActions>
        </Dialog>

        {/* Vendor Denial Dialog */}
        <Dialog open={denialDialog} onClose={() => setDenialDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Deny Vendor Registration</DialogTitle>
          <DialogContent>
            {selectedPendingVendor && (
              <Box>
                <Typography variant="h6" gutterBottom>
                  {selectedPendingVendor.business_name}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {selectedPendingVendor.firstName} {selectedPendingVendor.lastName} - {selectedPendingVendor.email}
                </Typography>
                <TextField
                  label="Denial Reason (Required)"
                  fullWidth
                  multiline
                  rows={3}
                  value={denialReason}
                  onChange={e => setDenialReason(e.target.value)}
                  sx={{ mt: 2, mb: 2 }}
                  placeholder="Please provide a reason for denial that will be sent to the vendor..."
                  required
                />
                <TextField
                  label="Internal Notes (Optional)"
                  fullWidth
                  multiline
                  rows={2}
                  value={denialNotes}
                  onChange={e => setDenialNotes(e.target.value)}
                  placeholder="Internal notes (not sent to vendor)..."
                />
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDenialDialog(false)}>Cancel</Button>
            <Button 
              onClick={() => handleDenyVendor(selectedPendingVendor?.vendor_id)} 
              variant="contained" 
              color="error"
              disabled={!denialReason.trim()}
            >
              Deny Vendor
            </Button>
          </DialogActions>
        </Dialog>

        {/* Bulk Approval Dialog */}
        <Dialog open={bulkApprovalDialog} onClose={() => setBulkApprovalDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Bulk Approve Vendors</DialogTitle>
          <DialogContent>
            <Typography variant="body1" gutterBottom>
              You are about to approve {selectedPendingVendors.length} vendor(s):
            </Typography>
            <Box sx={{ mb: 2, maxHeight: 200, overflow: 'auto' }}>
              {selectedPendingVendors.map(vendorId => {
                const vendor = pendingVendors.find(v => v.vendor_id === vendorId);
                return vendor ? (
                  <Typography key={vendorId} variant="body2" sx={{ py: 0.5 }}>
                    • {vendor.business_name} ({vendor.email})
                  </Typography>
                ) : null;
              })}
            </Box>
            <TextField
              label="Bulk Approval Notes (Optional)"
              fullWidth
              multiline
              rows={3}
              value={bulkApprovalNotes}
              onChange={e => setBulkApprovalNotes(e.target.value)}
              placeholder="Add notes that will apply to all approved vendors..."
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setBulkApprovalDialog(false)}>Cancel</Button>
            <Button 
              onClick={handleBulkApprove} 
              variant="contained" 
              color="primary"
            >
              Approve All {selectedPendingVendors.length} Vendors
            </Button>
          </DialogActions>
        </Dialog>

        {/* Booth Selection Dialog */}
        <Dialog
          open={boothSelectionDialog}
          onClose={() => {
            setBoothSelectionDialog(false);
            setSelectedVendorForBooth(null);
          }}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            Select Booth for {selectedVendorForBooth?.business_name}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Click on an available booth to select it. Blue booths are available, grey booths are taken.
              </Typography>
              <BoothSelectionMap 
                onSelect={(boothData) => handleBoothAssignmentChange(selectedVendorForBooth?.vendor_id, boothData)}
                initialMapData={booths}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={() => {
                setBoothSelectionDialog(false);
                setSelectedVendorForBooth(null);
              }}
            >
              Cancel
            </Button>
          </DialogActions>
        </Dialog>

        {/* Add Delete Payment Confirmation Dialog */}
        <Dialog
          open={deletePaymentDialog}
          onClose={() => setDeletePaymentDialog(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Confirm Payment Deletion</DialogTitle>
          <DialogContent>
            <Typography variant="subtitle1" color="error" gutterBottom>
              Warning: This action cannot be undone
            </Typography>
            <Typography variant="body1">
              You are about to delete {selectedPayments.length} payment record(s).
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Note: Succeeded payments that haven't been refunded cannot be deleted.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeletePaymentDialog(false)}>Cancel</Button>
            <Button 
              onClick={handleDeletePayments}
              color="error" 
              variant="contained"
            >
              Delete Payments
            </Button>
          </DialogActions>
        </Dialog>
      </Container>

      {/* Edit User Dialog */}
      <Dialog open={editUserDialog} onClose={() => setEditUserDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit User</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              label="Email"
              fullWidth
              value={editUserData.email}
              onChange={(e) => setEditUserData({ ...editUserData, email: e.target.value })}
              sx={{ mb: 2 }}
              type="email"
            />
            <TextField
              label="First Name"
              fullWidth
              value={editUserData.firstName}
              onChange={(e) => setEditUserData({ ...editUserData, firstName: e.target.value })}
              sx={{ mb: 2 }}
            />
            <TextField
              label="Last Name"
              fullWidth
              value={editUserData.lastName}
              onChange={(e) => setEditUserData({ ...editUserData, lastName: e.target.value })}
              sx={{ mb: 2 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditUserDialog(false)}>Cancel</Button>
          <Button onClick={handleUserUpdate} variant="contained" color="primary">
            Update User
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={resetPasswordDialog} onClose={() => setResetPasswordDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Reset Password for {selectedUser?.firstName} {selectedUser?.lastName}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {passwordError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {passwordError}
              </Alert>
            )}
            <TextField
              label="New Password"
              type="password"
              fullWidth
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              sx={{ mb: 2 }}
              helperText="Password must be at least 8 characters long"
            />
            <TextField
              label="Confirm Password"
              type="password"
              fullWidth
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              sx={{ mb: 2 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setResetPasswordDialog(false);
            setNewPassword('');
            setConfirmPassword('');
            setPasswordError('');
          }}>Cancel</Button>
          <Button onClick={handleResetPassword} variant="contained" color="primary">
            Reset Password
          </Button>
        </DialogActions>
      </Dialog>

      {/* Incomplete Help Dialog */}
      <Dialog open={helpDialogOpen} onClose={() => setHelpDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {getHelpContent(helpReason).title}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            {getHelpContent(helpReason).message}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHelpDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );

  return embedded ? body : <MainLayout>{body}</MainLayout>;
};

export default AdminDashboard;